/**
 * SLAM BB Mobile Analyzer v2.0
 * Biomechanics-Focused Player Development Tool
 *
 * Combines Batman-style comic overlays with OpenCV.js real-time tracking.
 * Based on the Chen video analyzer hybrid pipeline:
 * - Gemini one-shot semantic detection of anatomical landmarks
 * - OpenCV optical flow (Lucas-Kanade) tracking across frames
 * - Physics-based floating labels with collision detection
 *
 * Designed for:
 * - Joint kinematics analysis
 * - Technique breakdown
 * - Player development feedback
 * - Injury prevention screening
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Upload, Play, Pause, Volume2, VolumeX, RotateCcw,
  Scan, Maximize, Zap, Target, AlertTriangle, Activity,
  TrendingUp, Shield, FileText, MessageSquare, ChevronRight, ChevronLeft,
  Download, X as CloseIcon, Flame, Route, ScanEye, Box, Crosshair, Camera
} from 'lucide-react';
import { SlamLoader } from '../design-system/SlamLoader.jsx';
import { C } from '../design-system/primitives';
import { interpolateColorStops } from '../../utils/colorInterpolation';
import D4DPanel from './D4DPanel';
import GazeOverlayV2 from '../gaze/GazeOverlayV2';
import MultiFilePreview from './MultiFilePreview';
import CameraCapture from './CameraCapture';
import { AnalysisChat } from '../v2/analyzer/AnalysisChat';
import usePersistedState from '../../hooks/usePersistedState';

// Constants for tracking and physics
const TRACKING_RESOLUTION = 500;
const DEFAULT_CONFIDENCE = 0.8;
const FRICTION = 0.93;
const COLLISION_MARGIN_X = 14;
const COLLISION_MARGIN_Y = 12;
const SOFT_MARGIN_X = 100;
const SOFT_MARGIN_Y = 80;
const SOFT_REPULSION_STRENGTH = 0.25;
const LABEL_MIN_HEIGHT = 36;
const POINT_SAFETY_BUFFER = 40;
const INTRO_TRANSITION_DURATION = 800;

// Confidence color thresholds
const CONFIDENCE_COLORS = {
  high: { threshold: 0.75, color: '#22c55e', label: 'High' },    // Green
  medium: { threshold: 0.5, color: '#eab308', label: 'Medium' }, // Yellow/Amber
  low: { threshold: 0, color: '#ef4444', label: 'Low' }          // Red
};

/** Get confidence value with default fallback */
const getConfidence = (obj) => obj.confidence ?? DEFAULT_CONFIDENCE;

/** Get color based on confidence level */
const getConfidenceColor = (confidence) => {
  if (confidence >= CONFIDENCE_COLORS.high.threshold) return CONFIDENCE_COLORS.high.color;
  if (confidence >= CONFIDENCE_COLORS.medium.threshold) return CONFIDENCE_COLORS.medium.color;
  return CONFIDENCE_COLORS.low.color;
};

/**
 * Get confidence label
 */
const getConfidenceLabel = (confidence) => {
  if (confidence >= CONFIDENCE_COLORS.high.threshold) return CONFIDENCE_COLORS.high.label;
  if (confidence >= CONFIDENCE_COLORS.medium.threshold) return CONFIDENCE_COLORS.medium.label;
  return CONFIDENCE_COLORS.low.label;
};

// Heatmap constants
const HEATMAP_GRID_SIZE = 50;
const HEATMAP_GAUSSIAN_RADIUS = 2;
const HEATMAP_GAUSSIAN_SIGMA = 1.5;
const HEATMAP_COLOR_STOPS = [
  { pos: 0.0, r: 0, g: 0, b: 255 },     // Blue
  { pos: 0.25, r: 0, g: 255, b: 255 },   // Cyan
  { pos: 0.5, r: 0, g: 255, b: 0 },      // Green
  { pos: 0.75, r: 255, g: 255, b: 0 },    // Yellow
  { pos: 1.0, r: 255, g: 0, b: 0 },       // Red
];

const HEATMAP_INFER_PATTERNS = [
  /heat\s*map/i,
  /\bwhere\b/i,
  /movement\s*pattern/i,
  /\btrajectory\b/i,
  /spatial/i,
  /position\s*frequency/i,
  /spend.*most.*time/i,
  /concentration/i,
  /\bhot\s*zone/i,
];

function getHeatmapColor(normalized) {
  const t = Math.max(0, Math.min(1, normalized));
  const { r, g, b } = interpolateColorStops(HEATMAP_COLOR_STOPS, t);
  const alpha = 0.15 + t * 0.55; // 0.15 at min, 0.7 at max
  return `rgba(${r},${g},${b},${alpha})`;
}

function promptInfersHeatmap(prompt) {
  if (!prompt || typeof prompt !== 'string') return false;
  return HEATMAP_INFER_PATTERNS.some(p => p.test(prompt));
}

// Enhanced tracking parameters
const LK_WIN_SIZE = 31;           // Larger window for better tracking (was 21)
const LK_MAX_LEVEL = 4;           // More pyramid levels (was 3)
const LK_MAX_ITER = 50;           // More iterations (was 30)
const LK_EPSILON = 0.001;         // Tighter epsilon (was 0.01)
const FB_ERROR_THRESHOLD = 1.5;   // Forward-backward error threshold in pixels (tightened from 3.0)
const MAX_POINT_VELOCITY = 80;    // Max pixels per frame movement
const DRIFT_REDETECT_FRAMES = 45; // Re-detect every ~1.5 seconds at 30fps (was 90)
const STATIC_NODE_THRESHOLD = 20; // Min displacement in 0-1000 space to be "moving" (~2% of frame)
const PATH_HISTORY_MAX_LENGTH = 150; // ~5 seconds at 30fps
const PATH_TRACE_LINE_WIDTH = 2;
const PATH_TRACE_MIN_OPACITY = 0.05;
const PATH_TRACE_MAX_OPACITY = 0.7;
const OVERLAY_UI_COMMIT_MS = 16; // ~60fps overlay updates — sub-frame interpolation handles smoothness
const REDETECT_BLEND_FRAMES = 8; // Smooth blending over ~8 frames when re-detecting (no hard snap)

// Skeletal distance constraint pairs — connected landmarks for anatomical consistency
const SKELETAL_CONSTRAINT_PAIRS = [
  ['Head/eyes', 'Right shoulder'],
  ['Head/eyes', 'Left shoulder'],
  ['Right shoulder', 'Right elbow'],
  ['Right elbow', 'Right wrist'],
  ['Left shoulder', 'Left elbow'],
  ['Left elbow', 'Left wrist'],
  ['Hips', 'Left knee'],
  ['Hips', 'Right knee'],
  ['Left knee', 'Left ankle'],
  ['Right knee', 'Right ankle'],
];
const SKELETAL_DEVIATION_THRESHOLD = 0.30; // 30% deviation triggers correction

/**
 * One Euro Filter — adaptive low-pass filter for real-time signal smoothing.
 * Ref: Casiez et al., CHI 2012. Used by MediaPipe Pose, Apple ARKit, Roboflow.
 *
 * Key insight: cutoff frequency adapts to signal velocity.
 * - Stationary → high smoothing (eliminates micro-jitter)
 * - Fast motion → low smoothing (preserves responsiveness)
 */
class OneEuroFilter {
  constructor(minCutoff = 0.5, beta = 0.007, dCutoff = 1.0) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }

  static _alpha(cutoff, te) {
    const tau = 1.0 / (2.0 * Math.PI * cutoff);
    return 1.0 / (1.0 + tau / te);
  }

  static _lowPass(alpha, x, xPrev) {
    return alpha * x + (1.0 - alpha) * xPrev;
  }

  apply(x, t) {
    if (this.tPrev === null) {
      this.xPrev = x;
      this.tPrev = t;
      this.dxPrev = 0;
      return x;
    }

    const te = t - this.tPrev;
    if (te <= 0) return this.xPrev; // guard against zero/negative dt

    // Derivative estimation with its own low-pass filter
    const dx = (x - this.xPrev) / te;
    const dAlpha = OneEuroFilter._alpha(this.dCutoff, te);
    const dxHat = OneEuroFilter._lowPass(dAlpha, dx, this.dxPrev);

    // Adaptive cutoff based on signal velocity
    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const alpha = OneEuroFilter._alpha(cutoff, te);
    const xHat = OneEuroFilter._lowPass(alpha, x, this.xPrev);

    this.xPrev = xHat;
    this.dxPrev = dxHat;
    this.tPrev = t;

    return xHat;
  }

  reset() {
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }
}
/**
 * Savitzky-Golay filter — polynomial-fit smoothing for trajectory post-processing.
 * Applies a least-squares polynomial fit over a sliding window.
 * Window must be odd, polyOrder < windowSize.
 *
 * Used for path trace smoothing: removes tracking jitter while preserving
 * the true shape of biomechanical motion (e.g., arm arc, knee drive).
 */
function savgolSmooth1D(data, windowSize = 7, polyOrder = 2) {
  if (!data || data.length < windowSize) return data;

  const halfWin = Math.floor(windowSize / 2);
  const result = new Array(data.length);

  // Pre-compute Vandermonde coefficients for the window
  const coeffs = savgolCoeffs(windowSize, polyOrder);

  for (let i = 0; i < data.length; i++) {
    let val = 0;
    for (let j = -halfWin; j <= halfWin; j++) {
      // Mirror boundary handling
      let idx = i + j;
      if (idx < 0) idx = -idx;
      if (idx >= data.length) idx = 2 * (data.length - 1) - idx;
      idx = Math.max(0, Math.min(data.length - 1, idx));
      val += coeffs[j + halfWin] * data[idx];
    }
    result[i] = val;
  }
  return result;
}

/** Compute Savitzky-Golay convolution coefficients via least-squares. */
function savgolCoeffs(windowSize, polyOrder) {
  const halfWin = Math.floor(windowSize / 2);
  const n = windowSize;

  // Build Vandermonde matrix J
  const J = [];
  for (let i = -halfWin; i <= halfWin; i++) {
    const row = [];
    for (let p = 0; p <= polyOrder; p++) {
      row.push(Math.pow(i, p));
    }
    J.push(row);
  }

  // Compute (J^T J)^-1 J^T — first row gives smoothing coefficients
  const JT = transpose(J);
  const JTJ = matMul(JT, J);
  const JTJinv = invertSmall(JTJ);
  if (!JTJinv) {
    // Fallback: uniform average
    return new Array(n).fill(1 / n);
  }
  const C = matMul(JTJinv, JT);
  return C[0]; // First row = smoothing (0th derivative)
}

function transpose(m) {
  return m[0].map((_, j) => m.map(row => row[j]));
}

function matMul(a, b) {
  const rows = a.length, cols = b[0].length, inner = b.length;
  const r = Array.from({ length: rows }, () => new Array(cols).fill(0));
  for (let i = 0; i < rows; i++)
    for (let j = 0; j < cols; j++)
      for (let k = 0; k < inner; k++)
        r[i][j] += a[i][k] * b[k][j];
  return r;
}

/** Invert a small square matrix (up to ~5x5) using Gauss-Jordan. */
function invertSmall(m) {
  const n = m.length;
  const aug = m.map((row, i) => [...row, ...Array.from({ length: n }, (_, j) => i === j ? 1 : 0)]);
  for (let col = 0; col < n; col++) {
    let maxRow = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(aug[row][col]) > Math.abs(aug[maxRow][col])) maxRow = row;
    }
    [aug[col], aug[maxRow]] = [aug[maxRow], aug[col]];
    if (Math.abs(aug[col][col]) < 1e-12) return null;
    const pivot = aug[col][col];
    for (let j = 0; j < 2 * n; j++) aug[col][j] /= pivot;
    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const factor = aug[row][col];
      for (let j = 0; j < 2 * n; j++) aug[row][j] -= factor * aug[col][j];
    }
  }
  return aug.map(row => row.slice(n));
}

/** Apply SavGol smoothing to a 2D path of [y, x] points. */
function savgolSmoothPath(path, windowSize = 7, polyOrder = 2) {
  if (!path || path.length < windowSize) return path;
  const ys = savgolSmooth1D(path.map(p => p[0]), windowSize, polyOrder);
  const xs = savgolSmooth1D(path.map(p => p[1]), windowSize, polyOrder);
  return ys.map((y, i) => [y, xs[i]]);
}

const MAX_OVERLAY_DPR = 2; // Balance sharpness and canvas fill cost

function syncOverlayCanvasToContainer(canvas, container, clearOnResize = true) {
  if (!canvas || !container) return null;

  const rect = container.getBoundingClientRect();
  const width = Math.max(1, Math.floor(rect.width));
  const height = Math.max(1, Math.floor(rect.height));
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_OVERLAY_DPR);
  const nextPixelWidth = Math.max(1, Math.floor(width * dpr));
  const nextPixelHeight = Math.max(1, Math.floor(height * dpr));
  const resized = canvas.width !== nextPixelWidth || canvas.height !== nextPixelHeight;

  if (resized) {
    canvas.width = nextPixelWidth;
    canvas.height = nextPixelHeight;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
  }

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  if (resized && clearOnResize) {
    ctx.clearRect(0, 0, width, height);
  }

  return { ctx, width, height, resized };
}

// API configuration - uses proxy in development, explicit URL in production
const API_BASE = process.env.REACT_APP_API_URL || '';

// Sport-specific configurations
const SPORT_CONFIGS = {
  baseball: {
    name: 'Baseball/Softball',
    icon: '⚾',
    focusAreas: ['Arm Slot', 'Hip-Shoulder Separation', 'Release Point', 'Stride Length'],
    color: '#DC2626'
  },
  basketball: {
    name: 'Basketball',
    icon: '🏀',
    focusAreas: ['Shooting Form', 'Jump Mechanics', 'Landing Patterns', 'Defensive Stance'],
    color: '#EA580C'
  },
  football: {
    name: 'Football',
    icon: '🏈',
    focusAreas: ['Throwing Mechanics', 'Footwork', 'Pad Level', 'Hip Rotation'],
    color: '#65A30D'
  },
  soccer: {
    name: 'Soccer',
    icon: '⚽',
    focusAreas: ['Plant Foot', 'Follow Through', 'Body Position', 'Balance'],
    color: '#059669'
  },
  track: {
    name: 'Track & Field',
    icon: '🏃',
    focusAreas: ['Stride Mechanics', 'Arm Action', 'Ground Contact', 'Body Lean'],
    color: '#0891B2'
  },
  general: {
    name: 'General',
    icon: '🏋️',
    focusAreas: ['Joint Angles', 'Balance', 'Power Output', 'Symmetry'],
    color: '#FFD700'
  }
};

/** Simple hook-free check: is the current viewport narrow (mobile)? */
function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
}

/**
 * Capitalize first letter
 */
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Calculate centroid of detected objects
 */
const calculateCentroid = (objects) => {
  if (!objects || objects.length === 0) return { x: 500, y: 500 };
  const sum = objects.reduce((acc, obj) => ({
    x: acc.x + (obj.point?.[1] || 500),
    y: acc.y + (obj.point?.[0] || 500)
  }), { x: 0, y: 0 });
  return {
    x: sum.x / objects.length,
    y: sum.y / objects.length
  };
};

/**
 * Get distance to rectangle edge from center
 */
const getDistToRectEdge = (dirX, dirY, w, h) => {
  return Math.min(
    (w / 2) / (Math.abs(dirX) || 0.0001),
    (h / 2) / (Math.abs(dirY) || 0.0001)
  );
};

/**
 * Calculate dynamic offset for label placement
 */
const getDynamicOffset = (point, centroid, stageSize, w, h) => {
  const y = point[0];
  const x = point[1];
  let dx = x - centroid.x;
  let dy = y - centroid.y;

  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
    dx = x < 500 ? -1 : 1;
    dy = y < 500 ? -1 : 1;
  }

  const mag = Math.sqrt(dx * dx + dy * dy) || 1;
  const dirX = dx / mag;
  const dirY = dy / mag;

  const distToEdge = getDistToRectEdge(dirX, dirY, w, h);
  const targetDistance = Math.max(stageSize * 0.15, distToEdge + POINT_SAFETY_BUFFER + 60);

  return {
    relX: dirX * targetDistance,
    relY: dirY * targetDistance
  };
};

/**
 * Get local average color from canvas context
 */
const getLocalAverageColor = (ctx, x, y) => {
  const radius = 10;
  try {
    const data = ctx.getImageData(Math.max(0, x - radius), Math.max(0, y - radius), radius * 2, radius * 2).data;
    let r = 0, g = 0, b = 0, count = 0;
    for (let i = 0; i < data.length; i += 4) {
      r += data[i]; g += data[i+1]; b += data[i+2];
      count++;
    }
    return "#" + [r, g, b].map(v => Math.round(v/count).toString(16).padStart(2, '0')).join('');
  } catch {
    return '#FFD700';
  }
};

/**
 * Modify color lightness and saturation
 */
const modifyColor = (hex, lightnessAdd, saturationAdd) => {
  try {
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;

    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s, l = (max + min) / 2;

    if (max !== min) {
      let d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: break;
      }
      h /= 6;
    } else {
      s = 0;
    }

    l = Math.min(1, l + lightnessAdd);
    s = Math.min(1, s + saturationAdd);

    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    let p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);

    const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } catch {
    return hex;
  }
};

/**
 * Interpolate between two colors
 */
const interpolateColor = (color1, color2, factor) => {
  try {
    const hex = (x) => parseInt(x.replace('#', ''), 16);
    const r1 = (hex(color1) >> 16) & 255;
    const g1 = (hex(color1) >> 8) & 255;
    const b1 = hex(color1) & 255;
    const r2 = (hex(color2) >> 16) & 255;
    const g2 = (hex(color2) >> 8) & 255;
    const b2 = hex(color2) & 255;
    const r = Math.round(r1 + (r2 - r1) * factor);
    const g = Math.round(g1 + (g2 - g1) * factor);
    const b = Math.round(b1 + (b2 - b1) * factor);
    return `rgb(${r}, ${g}, ${b})`;
  } catch {
    return color1;
  }
};

/**
 * Typewriter text effect component
 */
const TypewriterText = ({ text, delay = 14, onComplete }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    setCount(0);
    if (!text) return;
    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev >= text.length) {
          clearInterval(interval);
          onComplete?.();
          return prev;
        }
        return prev + 1;
      });
    }, delay);
    return () => clearInterval(interval);
  }, [text, delay, onComplete]);
  return <span style={{ whiteSpace: 'pre-wrap' }}>{text.slice(0, count)}</span>;
};

/**
 * Clean, minimal label component mimicking the cat image style
 * Features: thin connecting lines, small labels, typewriter text
 */
const BiomechanicsLabel = ({
  obj,
  index,
  isVisible,
  labelPosition,
  pointPosition,
  color,
  hasFinishedTyping,
  onTypeComplete,
  onPointerDown,
  onPointerUp,
  isBatmanMode,
  sportColor,
  stageSize,
  showConfidence = true
}) => {
  if (!isVisible) return null;

  // Calculate scaled point position
  const scaledPointX = pointPosition ? (pointPosition[1] / 1000) * stageSize : 0;
  const scaledPointY = pointPosition ? (pointPosition[0] / 1000) * stageSize : 0;

  // Get confidence-based color (default to 0.8 if not provided)
  const confidence = getConfidence(obj);
  const confidenceColor = getConfidenceColor(confidence);
  const confidenceLabel = getConfidenceLabel(confidence);

  const mainColor = sportColor || 'rgba(255, 255, 255, 0.9)';
  const lineColor = showConfidence ? `${confidenceColor}99` : 'rgba(255, 255, 255, 0.4)';

  if (isBatmanMode) {
    // Batman comic style - for key moments (kept for toggle)
    const burstFill = confidence >= 0.75 ? '#f97316' : confidence >= 0.5 ? '#ef4444' : '#7f1d1d';
    return (
      <div
        className="absolute flex items-center justify-center pointer-events-none"
        style={{
          left: `${labelPosition.x}px`,
          top: `${labelPosition.y}px`,
          transform: 'translate(-50%, -50%)',
          zIndex: 50
        }}
      >
        <svg viewBox="0 0 200 100" className="absolute w-[180%] h-[180%] opacity-90 z-0 drop-shadow-xl">
          <path
            d="M10,50 Q30,20 50,40 T90,30 T130,40 T170,20 T190,50 Q170,80 150,60 T110,70 T70,60 T30,80 Z"
            fill={burstFill}
            stroke="black"
            strokeWidth="3"
          />
        </svg>
        <h2
          className="relative z-10 font-black text-xl uppercase leading-none text-center whitespace-nowrap transform -rotate-3"
          style={{
            color: '#FFD700',
            WebkitTextStroke: '1.5px black',
            textShadow: '3px 3px 0px #000'
          }}
        >
          {obj.label}
        </h2>
        <div
          className="absolute -bottom-4 px-2 py-0.5 rounded-full text-[9px] font-black tracking-wide"
          style={{
            color: '#111',
            background: confidenceColor,
            border: '2px solid #000',
            textTransform: 'uppercase',
          }}
        >
          {Math.round(confidence * 100)}%
        </div>
      </div>
    );
  }

  // Clean minimal style - like the cat image
  return (
    <>
      {/* Connecting Line from label to point */}
      <svg
        className="absolute pointer-events-none"
        style={{
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          zIndex: 35,
          overflow: 'visible'
        }}
      >
        <line
          x1={labelPosition.x}
          y1={labelPosition.y}
          x2={scaledPointX}
          y2={scaledPointY}
          stroke={lineColor}
          strokeWidth="1"
          strokeDasharray="none"
        />
      </svg>

      {/* Circle indicator at the point - color-coded by confidence */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: `${scaledPointX}px`,
          top: `${scaledPointY}px`,
          transform: 'translate(-50%, -50%)',
          zIndex: 36
        }}
      >
        {/* Outer ring - confidence colored */}
        <div
          className="w-8 h-8 rounded-full border-2"
          style={{
            borderColor: showConfidence ? confidenceColor : 'white',
            opacity: showConfidence ? 0.7 : 0.4,
            boxShadow: showConfidence ? `0 0 8px ${confidenceColor}50` : 'none'
          }}
        />
        {/* Inner dot - confidence colored */}
        <div
          className="absolute w-2.5 h-2.5 rounded-full"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: showConfidence ? confidenceColor : 'white'
          }}
        />
      </div>

      {/* Label box - compact and clean with confidence indicator */}
      <div
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        className="absolute max-w-[200px] backdrop-blur-sm px-2 py-1.5 rounded flex flex-col z-40 pointer-events-auto cursor-grab active:cursor-grabbing transition-opacity duration-300"
        style={{
          left: `${labelPosition.x}px`,
          top: `${labelPosition.y}px`,
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(30, 30, 30, 0.85)',
          borderLeft: showConfidence ? `3px solid ${confidenceColor}` : 'none',
        }}
      >
        {/* Title with confidence indicator */}
        <div className="flex items-center gap-1.5">
          <span
            className="font-semibold text-[11px] leading-tight"
            style={{ color: 'rgba(255, 255, 255, 0.95)' }}
          >
            {capitalizeFirst(obj.label)}
          </span>
          {showConfidence && (
            <span
              className="text-[8px] font-medium px-1 py-0.5 rounded"
              style={{
                backgroundColor: `${confidenceColor}30`,
                color: confidenceColor
              }}
            >
              {Math.round(confidence * 100)}%
            </span>
          )}
        </div>

        {/* Description - typewriter effect */}
        <span className="text-[10px] leading-snug select-none" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {hasFinishedTyping ? (
            <span style={{ whiteSpace: 'pre-wrap' }}>{obj.description}</span>
          ) : (
            <TypewriterText text={obj.description} delay={18} onComplete={onTypeComplete} />
          )}
        </span>
      </div>
    </>
  );
};

/**
 * Sport Selector Component
 */
const SportSelector = ({ selectedSport, onSelectSport }) => {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
      {Object.entries(SPORT_CONFIGS).map(([key, config]) => (
        <button
          key={key}
          onClick={() => onSelectSport(key)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
            selectedSport === key
              ? 'bg-amber-400 text-black'
              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
          }`}
        >
          <span>{config.icon}</span>
          <span>{config.name}</span>
        </button>
      ))}
    </div>
  );
};

/**
 * Analysis Panel - Shows detected landmarks with selection filtering and expandable view
 */
const AnalysisPanel = ({
  objects,
  sport,
  customFocus,
  onJumpToObject,
  selectedIndices,
  onToggleSelect,
  onClearSelection,
  isExpanded,
  onToggleExpand,
  onExportReport,
  isMobile = false,
}) => {
  const sportConfig = SPORT_CONFIGS[sport] || SPORT_CONFIGS.general;
  const hasSelection = selectedIndices && selectedIndices.size > 0;
  const [expandedCards, setExpandedCards] = useState(new Set());

  // Toggle card expansion (separate from selection)
  const toggleCardExpand = (index, e) => {
    e.stopPropagation(); // Don't trigger selection
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Memoize confidence stats to avoid recalculation on every render
  const confidenceStats = useMemo(() => {
    if (objects.length === 0) {
      return { avgConfidence: 0, highConfCount: 0, medConfCount: 0, lowConfCount: 0 };
    }
    let sum = 0, high = 0, med = 0, low = 0;
    for (const obj of objects) {
      const c = getConfidence(obj);
      sum += c;
      if (c >= CONFIDENCE_COLORS.high.threshold) high++;
      else if (c >= CONFIDENCE_COLORS.medium.threshold) med++;
      else low++;
    }
    return {
      avgConfidence: sum / objects.length,
      highConfCount: high, medConfCount: med, lowConfCount: low,
    };
  }, [objects]);

  const { avgConfidence, highConfCount, medConfCount, lowConfCount } = confidenceStats;

  return (
    <div className={`flex flex-col bg-zinc-950 ${isMobile ? 'border-t border-zinc-800' : 'h-full border-l border-zinc-800'}`}>
      {/* Header with expand toggle */}
      <div className="p-4 border-b border-zinc-800 bg-zinc-900/50 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-400" />
            <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-bold text-white uppercase tracking-wider`}>
              Biomechanics <span className="text-amber-400">Analysis</span>
            </h2>
          </div>
          {onToggleExpand && (
            <button
              onClick={onToggleExpand}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
              title={isExpanded ? 'Collapse panel' : 'Expand panel'}
            >
              <Maximize size={16} />
            </button>
          )}
        </div>
        <div className="text-[10px] text-zinc-500 font-mono">
          {sportConfig.icon} {sportConfig.name.toUpperCase()} • {objects.length} LANDMARKS DETECTED
        </div>
      </div>

      {/* Confidence Summary Bar */}
      {objects.length > 0 && (
        <div className="px-4 py-2 border-b border-zinc-800 bg-zinc-900/30">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Signal Confidence</span>
            <span className="text-[10px] font-mono" style={{ color: getConfidenceColor(avgConfidence) }}>
              {Math.round(avgConfidence * 100)}% avg
            </span>
          </div>
          <div className="flex gap-2 text-[9px]">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CONFIDENCE_COLORS.high.color }} />
              <span className="text-zinc-400">{highConfCount} high</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CONFIDENCE_COLORS.medium.color }} />
              <span className="text-zinc-400">{medConfCount} med</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CONFIDENCE_COLORS.low.color }} />
              <span className="text-zinc-400">{lowConfCount} low</span>
            </span>
          </div>
        </div>
      )}

      {/* Focus Areas */}
      <div className="p-3 border-b border-zinc-800 bg-zinc-900/30">
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Focus Areas</div>
        <div className="flex flex-wrap gap-1.5">
          {customFocus && (
            <span className="px-2 py-0.5 bg-amber-400/20 border border-amber-400/40 rounded text-[10px] text-amber-300 font-semibold">
              {customFocus}
            </span>
          )}
          {sportConfig.focusAreas.map((area, i) => (
            <span
              key={i}
              className="px-2 py-0.5 bg-zinc-800 rounded text-[10px] text-zinc-300"
            >
              {area}
            </span>
          ))}
        </div>
      </div>

      {/* Selection Controls */}
      {objects.length > 0 && (
        <div className="px-3 py-2 border-b border-zinc-800 bg-zinc-900/20 flex items-center justify-between">
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
            {hasSelection ? `${selectedIndices.size} SELECTED` : 'CLICK TO FILTER'}
          </span>
          {hasSelection && (
            <button
              onClick={onClearSelection}
              className="text-[10px] text-amber-400 hover:text-amber-300 uppercase tracking-wider"
            >
              Show All
            </button>
          )}
        </div>
      )}

      {/* Landmarks List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
        {objects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-600 gap-3">
            <Target size={32} className="opacity-20" />
            <p className="font-mono text-xs text-center">
              Upload a video and click<br />Analyze to detect landmarks
            </p>
          </div>
        ) : (
          objects.map((obj, i) => {
            const isSelected = selectedIndices?.has(i);
            const isFiltered = hasSelection && !isSelected;
            const confidence = getConfidence(obj);
            const confColor = getConfidenceColor(confidence);
            const isCardExpanded = expandedCards.has(i);

            return (
              <div
                key={i}
                className={`border rounded-lg transition-all group ${
                  isSelected
                    ? 'bg-amber-400/10 border-amber-400'
                    : isFiltered
                    ? 'bg-zinc-900/40 border-zinc-800/50 opacity-50'
                    : 'bg-zinc-900/80 border-zinc-800 hover:border-amber-400/50'
                }`}
                style={{
                  borderLeftWidth: '3px',
                  borderLeftColor: isSelected ? '#fbbf24' : confColor
                }}
              >
                {/* Header row - clicking checkbox toggles selection */}
                <div className="flex items-center gap-2 p-2.5 pb-1">
                  {/* Selection checkbox indicator */}
                  <div
                    onClick={() => onToggleSelect?.(i)}
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-amber-400 border-amber-400'
                        : 'border-zinc-600 hover:border-amber-400/50'
                    }`}
                  >
                    {isSelected && (
                      <svg className="w-2.5 h-2.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className={`text-sm font-semibold transition-colors ${
                    isSelected ? 'text-amber-400' : 'text-white'
                  }`}>
                    {obj.label}
                  </span>
                  {/* Confidence badge */}
                  <span
                    className="text-[9px] font-mono px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: `${confColor}20`,
                      color: confColor
                    }}
                  >
                    {Math.round(confidence * 100)}%
                  </span>
                  <span className="ml-auto text-[10px] text-zinc-500 font-mono">
                    [{obj.point[0].toFixed(0)}, {obj.point[1].toFixed(0)}]
                  </span>
                </div>
                {/* Description - clickable to expand/collapse */}
                <div
                  onClick={(e) => toggleCardExpand(i, e)}
                  className="px-2.5 pb-2.5 pt-1 cursor-pointer hover:bg-zinc-800/30 transition-colors rounded-b-lg"
                >
                  <p className={`text-[11px] text-zinc-400 leading-relaxed pl-6 ${isCardExpanded ? '' : 'line-clamp-2'}`}>
                    {obj.description}
                  </p>
                  {/* Expand/collapse indicator */}
                  {obj.description && obj.description.length > 80 && (
                    <div className="flex items-center justify-center mt-1.5 text-zinc-500">
                      <ChevronRight
                        size={12}
                        className={`transition-transform duration-200 ${isCardExpanded ? 'rotate-90' : ''}`}
                      />
                      <span className="text-[9px] ml-0.5">
                        {isCardExpanded ? 'Less' : 'More'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-3 border-t border-zinc-800 bg-zinc-950 flex-shrink-0 space-y-2">
        <button
          onClick={onExportReport}
          disabled={objects.length === 0}
          className="w-full py-2 bg-zinc-900 border border-zinc-800 text-zinc-400 font-medium text-sm uppercase tracking-wider hover:text-white hover:border-amber-400 transition-colors rounded disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <FileText size={14} />
          Export Report
        </button>
      </div>
    </div>
  );
};

/**
 * Main SLAM Mobile Analyzer Component
 */
const SlamMobileAnalyzer = () => {
  // Video state
  const [videoSrc, setVideoSrc] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Analysis state
  const [detectedObjects, setDetectedObjects] = useState([]);
  const [labelPositions, setLabelPositions] = useState([]);
  const [objectColors, setObjectColors] = useState([]);
  const [keylineColors, setKeylineColors] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [selectedLandmarks, setSelectedLandmarks] = useState(new Set()); // For filtering overlay
  const [analysisError, setAnalysisError] = useState(null);

  // UI state
  const [stageSize, setStageSize] = useState(0);
  const [videoNativeDims, setVideoNativeDims] = useState({ width: 0, height: 0 });
  const [isBatmanMode, setIsBatmanMode] = usePersistedState('slam_mobile_batmanMode', false);
  const [selectedSport, setSelectedSport] = usePersistedState('slam_mobile_analysisMode', 'general');
  const [showPanel, setShowPanel] = useState(() => !isMobileViewport());
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(() => isMobileViewport());
  const [lastFrameBase64, setLastFrameBase64] = useState(null); // For chat follow-up
  const [visibleLabelIndices, setVisibleLabelIndices] = useState(new Set());
  const [typedIndices, setTypedIndices] = useState(new Set());
  const [dotTransitionStartTimes, setDotTransitionStartTimes] = useState([]);
  const [resetKey, setResetKey] = useState(0);
  const [isOpenCvReady, setIsOpenCvReady] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [showPromptInput, setShowPromptInput] = useState(false);
  const [analysisSummary, setAnalysisSummary] = useState(null);

  // Multi-file upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [showCamera, setShowCamera] = useState(false);
  const [bundleAnalyzing, setBundleAnalyzing] = useState(false);
  const [bundleResults, setBundleResults] = useState(null);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const trackingCanvasRef = useRef(null);
  const stageContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  // (removed emulator ref)

  const lastTimeRef = useRef(0);
  const trackingPointsRef = useRef([]);
  const originalObjectsRef = useRef([]);
  const trackRequestRef = useRef(null);
  const physicsRequestRef = useRef(null);

  // OpenCV refs
  const cvPrevGrayRef = useRef(null);
  const cvPrevPtsRef = useRef(null);

  // Enhanced tracking refs
  const trackingFrameCountRef = useRef(0);
  const lastGoodPositionsRef = useRef([]);
  const smoothedPositionsRef = useRef([]);

  // One Euro Filter refs — per-landmark, per-axis (x and y independently)
  const oneEuroFiltersRef = useRef([]); // Array of { y: OneEuroFilter, x: OneEuroFilter }

  // Sub-frame interpolation refs — prev/curr positions with timestamps for lerping
  const prevPositionsRef = useRef([]);  // Previous tracked positions per landmark [y, x]
  const currPositionsRef = useRef([]);  // Current tracked positions per landmark [y, x]
  const lastTrackTimeRef = useRef(0);   // Timestamp of last tracking update
  const trackIntervalRef = useRef(33);  // Estimated interval between tracking updates (ms)

  // Re-detection blending refs — smooth transition when re-initializing points
  const redetectBlendRef = useRef([]); // Per-landmark: { from: [y,x], to: [y,x], frame: 0, total: REDETECT_BLEND_FRAMES }
  const initialDistancesRef = useRef(null); // Map of connected pairs → initial pixel distances for skeletal constraints

  // Physics refs
  const labelPhysicsRef = useRef([]);
  const dragStateRef = useRef(null);
  const mousePosRef = useRef({ x: 0, y: 0 });
  const lastLabelCommitTsRef = useRef(0);
  const lastDetectedCommitTsRef = useRef(0);

  // Heatmap state & refs
  const [isHeatmapEnabled, setIsHeatmapEnabled] = usePersistedState('slam_mobile_heatmapEnabled', false);

  // Path trace state & refs
  const [isPathTraceEnabled, setIsPathTraceEnabled] = usePersistedState('slam_mobile_pathTraceEnabled', false);
  const maxDisplacementRef = useRef([]);  // Per-node max displacement from original
  const pathHistoryRef = useRef([]);       // Per-node array of [y,x] position history
  const pathTraceCanvasRef = useRef(null);
  const pathTraceRafRef = useRef(null);
  const pathTraceDirtyRef = useRef(false);
  const pathTraceRenderScheduledRef = useRef(false);
  const isPathTraceEnabledRef = useRef(false);

  // 4D analysis state & refs
  const [is4DEnabled, setIs4DEnabled] = useState(false);
  const is4DEnabledRef = useRef(false);
  const heatmapGridRef = useRef(new Float64Array(HEATMAP_GRID_SIZE * HEATMAP_GRID_SIZE));
  const heatmapCanvasRef = useRef(null);
  const heatmapSampleCountRef = useRef(0);
  const heatmapDirtyRef = useRef(false);
  const heatmapRafRef = useRef(null);
  const heatmapRenderScheduledRef = useRef(false);
  const isHeatmapEnabledRef = useRef(false);
  const selectedLandmarksRef = useRef(new Set());

  // Markers toggle state — controls visibility of biomech landmark nodes/labels
  const [isMarkersEnabled, setIsMarkersEnabled] = usePersistedState('slam_mobile_markersEnabled', true);

  // Gaze tracking state
  const [isGazeEnabled, setIsGazeEnabled] = useState(false);
  const isGazeEnabledRef = useRef(false);

  // Gaze tracking is now handled by GazeOverlayV2 (MediaPipe client-side)
  // No separate hook needed — the overlay component manages its own model loading
  const isGazeLoading = false;
  const gazeProgress = null;

  // Keep refs in sync with state (avoids stale closures in RAF callbacks)
  useEffect(() => {
    isHeatmapEnabledRef.current = isHeatmapEnabled;
  }, [isHeatmapEnabled]);
  useEffect(() => {
    isPathTraceEnabledRef.current = isPathTraceEnabled;
  }, [isPathTraceEnabled]);
  useEffect(() => {
    is4DEnabledRef.current = is4DEnabled;
  }, [is4DEnabled]);
  useEffect(() => {
    isGazeEnabledRef.current = isGazeEnabled;
  }, [isGazeEnabled]);
  useEffect(() => {
    selectedLandmarksRef.current = selectedLandmarks;
  }, [selectedLandmarks]);

  // GazeOverlayV2 handles its own model loading when visible — no manual trigger needed

  const sportConfig = SPORT_CONFIGS[selectedSport] || SPORT_CONFIGS.general;

  // Check OpenCV availability
  useEffect(() => {
    const checkCV = () => {
      if (window.cv && window.cv.getBuildInformation) {
        setIsOpenCvReady(true);
        console.log('[SlamMobile] OpenCV.js ready');
      } else {
        setTimeout(checkCV, 100);
      }
    };
    checkCV();
  }, []);

  // Stage size observer
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setStageSize(entry.contentRect.width);
    });
    if (stageContainerRef.current) observer.observe(stageContainerRef.current);
    return () => observer.disconnect();
  }, []);

  // Staggered label reveal animation
  useEffect(() => {
    setVisibleLabelIndices(new Set());
    setTypedIndices(new Set());
    setDotTransitionStartTimes(Array(detectedObjects.length).fill(null));
    const timeouts = [];
    for (let i = 0; i < detectedObjects.length; i++) {
      const timeout = setTimeout(() => {
        setVisibleLabelIndices(prev => new Set([...prev, i]));
        setDotTransitionStartTimes(prev => {
          const next = [...prev];
          next[i] = Date.now();
          return next;
        });
      }, i * 150);
      timeouts.push(timeout);
    }
    return () => timeouts.forEach(t => clearTimeout(t));
  }, [resetKey, detectedObjects.length]);

  // Clear OpenCV memory
  const clearOpenCvMemory = useCallback(() => {
    if (cvPrevGrayRef.current) {
      try { cvPrevGrayRef.current.delete(); } catch {}
      cvPrevGrayRef.current = null;
    }
    if (cvPrevPtsRef.current) {
      try { cvPrevPtsRef.current.delete(); } catch {}
      cvPrevPtsRef.current = null;
    }
  }, []);

  // Clear heatmap data
  const clearHeatmapData = useCallback(() => {
    heatmapGridRef.current.fill(0);
    heatmapSampleCountRef.current = 0;
    heatmapDirtyRef.current = true; // force redraw to clear canvas
    const canvas = heatmapCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  const clearPathTraceData = useCallback(() => {
    maxDisplacementRef.current = [];
    pathHistoryRef.current = [];
    pathTraceDirtyRef.current = true;
    const canvas = pathTraceCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  // Reset analysis state (shared by single and multi-file flows)
  const resetAnalysisState = useCallback(() => {
    setDetectedObjects([]);
    setLastFrameBase64(null);
    setLabelPositions([]);
    setObjectColors([]);
    setKeylineColors([]);
    setSelectedLandmarks(new Set());
    trackingPointsRef.current = [];
    originalObjectsRef.current = [];
    labelPhysicsRef.current = [];
    lastGoodPositionsRef.current = [];
    smoothedPositionsRef.current = [];
    trackingFrameCountRef.current = 0;
    oneEuroFiltersRef.current = [];
    prevPositionsRef.current = [];
    currPositionsRef.current = [];
    lastTrackTimeRef.current = 0;
    redetectBlendRef.current = [];
    initialDistancesRef.current = null;
    setIsAnalyzing(false);
    setIsPlaying(false);
    setHasAnalyzed(false);
    setAnalysisError(null);
    setVisibleLabelIndices(new Set());
    setTypedIndices(new Set());
    setDotTransitionStartTimes([]);
    lastTimeRef.current = 0;
    setResetKey(0);
    clearOpenCvMemory();
    clearHeatmapData();
    setIsHeatmapEnabled(false);
    maxDisplacementRef.current = [];
    pathHistoryRef.current = [];
    pathTraceDirtyRef.current = true;
    setIsPathTraceEnabled(false);
  }, [clearOpenCvMemory, clearHeatmapData]);

  // Handle file upload (multi-file aware)
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    // Separate video from supplemental files
    const videoFile = files.find(f => f.type.startsWith('video/'));
    const otherFiles = files.filter(f => !f.type.startsWith('video/'));

    // If a video is selected, set it as primary
    if (videoFile) {
      if (videoSrc) URL.revokeObjectURL(videoSrc);
      setVideoSrc(URL.createObjectURL(videoFile));
      resetAnalysisState();
    }

    // Add all files (including video) to the bundle
    setUploadedFiles(prev => [...prev, ...files]);
    setBundleResults(null);

    if (event.target) event.target.value = '';
  };

  // Multi-file helpers
  const handleRemoveFile = useCallback((index) => {
    setUploadedFiles(prev => {
      const removed = prev[index];
      // If removing the current video, clear videoSrc
      if (removed?.type.startsWith('video/') && videoSrc) {
        const removedUrl = URL.createObjectURL(removed);
        // Check if this was the displayed video (best effort)
        URL.revokeObjectURL(removedUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, [videoSrc]);

  const handleRemoveUrl = useCallback((index) => {
    setUploadedUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddUrl = useCallback((url) => {
    setUploadedUrls(prev => [...prev, url]);
  }, []);

  const handleAddFiles = useCallback((newFiles) => {
    const videoFile = newFiles.find(f => f.type.startsWith('video/'));
    if (videoFile && !videoSrc) {
      setVideoSrc(URL.createObjectURL(videoFile));
      resetAnalysisState();
    }
    setUploadedFiles(prev => [...prev, ...newFiles]);
  }, [videoSrc, resetAnalysisState]);

  const handleCameraCapture = useCallback((file) => {
    if (file.type.startsWith('video/')) {
      if (videoSrc) URL.revokeObjectURL(videoSrc);
      setVideoSrc(URL.createObjectURL(file));
      resetAnalysisState();
    }
    setUploadedFiles(prev => [...prev, file]);
    setShowCamera(false);
  }, [videoSrc, resetAnalysisState]);

  // Heatmap renderer: draws only when marked dirty.
  const scheduleHeatmapRender = useCallback(() => {
    if (!isHeatmapEnabledRef.current || heatmapRenderScheduledRef.current) return;
    heatmapRenderScheduledRef.current = true;

    heatmapRafRef.current = requestAnimationFrame(() => {
      heatmapRenderScheduledRef.current = false;
      if (!isHeatmapEnabledRef.current || !heatmapDirtyRef.current) return;
      heatmapDirtyRef.current = false;

      const canvas = heatmapCanvasRef.current;
      const container = stageContainerRef.current;
      const synced = syncOverlayCanvasToContainer(canvas, container);
      if (!synced) return;

      const { ctx, width, height } = synced;
      ctx.clearRect(0, 0, width, height);

      const rawGrid = heatmapGridRef.current;

      // Temporal smoothing: apply a 3x3 box blur pass to the heatmap grid
      // This smooths spatial noise and produces a more natural heat distribution
      const grid = new Float64Array(rawGrid.length);
      const GS = HEATMAP_GRID_SIZE;
      for (let gy = 0; gy < GS; gy++) {
        for (let gx = 0; gx < GS; gx++) {
          let sum = 0, cnt = 0;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const ny = gy + dy, nx = gx + dx;
              if (ny >= 0 && ny < GS && nx >= 0 && nx < GS) {
                sum += rawGrid[ny * GS + nx];
                cnt++;
              }
            }
          }
          grid[gy * GS + gx] = sum / cnt;
        }
      }

      let maxVal = 0;
      for (let i = 0; i < grid.length; i++) {
        if (grid[i] > maxVal) maxVal = grid[i];
      }
      if (maxVal === 0) return;

      const cellW = width / HEATMAP_GRID_SIZE;
      const cellH = height / HEATMAP_GRID_SIZE;
      for (let gy = 0; gy < HEATMAP_GRID_SIZE; gy++) {
        for (let gx = 0; gx < HEATMAP_GRID_SIZE; gx++) {
          const val = grid[gy * HEATMAP_GRID_SIZE + gx];
          if (val <= 0) continue;
          const normalized = val / maxVal;
          ctx.fillStyle = getHeatmapColor(normalized);
          ctx.fillRect(gx * cellW, gy * cellH, cellW + 0.5, cellH + 0.5);
        }
      }
    });
  }, []);

  // Path trace renderer: draws only when marked dirty.
  const schedulePathTraceRender = useCallback(() => {
    if (!isPathTraceEnabledRef.current || pathTraceRenderScheduledRef.current) return;
    pathTraceRenderScheduledRef.current = true;

    pathTraceRafRef.current = requestAnimationFrame(() => {
      pathTraceRenderScheduledRef.current = false;
      if (!isPathTraceEnabledRef.current || !pathTraceDirtyRef.current) return;
      pathTraceDirtyRef.current = false;

      const canvas = pathTraceCanvasRef.current;
      const container = stageContainerRef.current;
      const synced = syncOverlayCanvasToContainer(canvas, container);
      if (!synced) return;

      const { ctx, width, height } = synced;
      ctx.clearRect(0, 0, width, height);

      const history = pathHistoryRef.current;
      const objects = trackingPointsRef.current;
      if (!history || history.length === 0) return;

      const colorCache = new Map();
      const getRgb = (hex) => {
        if (colorCache.has(hex)) return colorCache.get(hex);
        const rgb = {
          r: parseInt(hex.slice(1, 3), 16),
          g: parseInt(hex.slice(3, 5), 16),
          b: parseInt(hex.slice(5, 7), 16),
        };
        colorCache.set(hex, rgb);
        return rgb;
      };

      for (let i = 0; i < history.length; i++) {
        const rawPath = history[i];
        if (!rawPath || rawPath.length < 2) continue;

        const displacement = maxDisplacementRef.current[i] || 0;
        if (displacement < STATIC_NODE_THRESHOLD) continue;

        // Apply Savitzky-Golay temporal smoothing to the path trace
        // This removes tracking jitter while preserving biomechanical motion shape
        const nodePath = rawPath.length >= 7 ? savgolSmoothPath(rawPath, 7, 2) : rawPath;

        const obj = objects[i];
        const confidence = obj?.confidence ?? DEFAULT_CONFIDENCE;
        const color = getConfidenceColor(confidence);
        const rgb = getRgb(color);

        for (let j = 1; j < nodePath.length; j++) {
          const progress = j / nodePath.length;
          const alpha = PATH_TRACE_MIN_OPACITY + progress * (PATH_TRACE_MAX_OPACITY - PATH_TRACE_MIN_OPACITY);
          const prevPt = nodePath[j - 1];
          const currPt = nodePath[j];

          const x1 = (prevPt[1] / 1000) * width;
          const y1 = (prevPt[0] / 1000) * height;
          const x2 = (currPt[1] / 1000) * width;
          const y2 = (currPt[0] / 1000) * height;

          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.strokeStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
          ctx.lineWidth = PATH_TRACE_LINE_WIDTH * (0.5 + progress * 0.5);
          ctx.lineCap = 'round';
          ctx.stroke();
        }

        const newest = nodePath[nodePath.length - 1];
        const nx = (newest[1] / 1000) * width;
        const ny = (newest[0] / 1000) * height;
        ctx.beginPath();
        ctx.arc(nx, ny, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},0.9)`;
        ctx.fill();
      }
    });
  }, []);

  // Toggle heatmap renderer lifecycle.
  useEffect(() => {
    if (!isHeatmapEnabled) {
      if (heatmapRafRef.current) {
        cancelAnimationFrame(heatmapRafRef.current);
        heatmapRafRef.current = null;
      }
      heatmapRenderScheduledRef.current = false;
      const canvas = heatmapCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }
    heatmapDirtyRef.current = true;
    scheduleHeatmapRender();
  }, [isHeatmapEnabled, scheduleHeatmapRender]);

  // Toggle path renderer lifecycle.
  useEffect(() => {
    if (!isPathTraceEnabled) {
      if (pathTraceRafRef.current) {
        cancelAnimationFrame(pathTraceRafRef.current);
        pathTraceRafRef.current = null;
      }
      pathTraceRenderScheduledRef.current = false;
      const canvas = pathTraceCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }
    pathTraceDirtyRef.current = true;
    schedulePathTraceRender();
  }, [isPathTraceEnabled, schedulePathTraceRender]);

  // Unmount cleanup: cancel any in-flight overlay RAFs
  useEffect(() => {
    return () => {
      if (heatmapRafRef.current) cancelAnimationFrame(heatmapRafRef.current);
      if (pathTraceRafRef.current) cancelAnimationFrame(pathTraceRafRef.current);
    };
  }, []);

  // Redraw overlays on stage resize (once), not every frame.
  useEffect(() => {
    heatmapDirtyRef.current = true;
    pathTraceDirtyRef.current = true;
    scheduleHeatmapRender();
    schedulePathTraceRender();
  }, [stageSize, scheduleHeatmapRender, schedulePathTraceRender]);

  // Toggle play/pause
  const togglePlay = (e) => {
    e?.stopPropagation();
    if (!videoSrc || !videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Toggle mute
  const toggleMute = (e) => {
    e?.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
    setIsMuted(!isMuted);
  };

  // Reset to original positions
  const resetToOriginal = useCallback(() => {
    setResetKey(prev => prev + 1);
    if (originalObjectsRef.current.length > 0) {
      const resetData = JSON.parse(JSON.stringify(originalObjectsRef.current));
      const centroid = calculateCentroid(resetData);
      setDetectedObjects(resetData);
      trackingPointsRef.current = resetData;
      labelPhysicsRef.current = labelPhysicsRef.current.map((lp, i) => {
        const ax = resetData[i].point[1] * (stageSize / 1000);
        const ay = resetData[i].point[0] * (stageSize / 1000);
        const offset = getDynamicOffset(resetData[i].point, centroid, stageSize, lp.w, lp.h);
        return { ...lp, relX: offset.relX, relY: offset.relY, x: ax + offset.relX, y: ay + offset.relY, vx: 0, vy: 0 };
      });
      // Reset enhanced tracking state
      lastGoodPositionsRef.current = resetData.map(obj => [...obj.point]);
      smoothedPositionsRef.current = resetData.map(obj => [...obj.point]);
      trackingFrameCountRef.current = 0;
      // Reset One Euro Filters
      oneEuroFiltersRef.current = resetData.map(() => ({
        y: new OneEuroFilter(0.5, 0.007, 1.0),
        x: new OneEuroFilter(0.5, 0.007, 1.0),
      }));
      // Reset sub-frame interpolation
      prevPositionsRef.current = resetData.map(obj => [...obj.point]);
      currPositionsRef.current = resetData.map(obj => [...obj.point]);
      lastTrackTimeRef.current = performance.now();
      // Reset re-detection blend
      redetectBlendRef.current = resetData.map(() => null);
      // Reset skeletal constraints (will be re-computed on first tracking frame)
      initialDistancesRef.current = null;
      clearOpenCvMemory();
      clearHeatmapData();
      // Clear path trace + displacement tracking
      maxDisplacementRef.current = [];
      pathHistoryRef.current = [];
      pathTraceDirtyRef.current = true;
      scheduleHeatmapRender();
      schedulePathTraceRender();
    }
  }, [stageSize, clearOpenCvMemory, clearHeatmapData, scheduleHeatmapRender, schedulePathTraceRender]);

  // Restart video
  const restart = (e) => {
    e?.stopPropagation();
    if (!videoSrc || !videoRef.current) return;
    videoRef.current.currentTime = 0;
    resetToOriginal();
    videoRef.current.play();
    setIsPlaying(true);
  };

  const clearVisualOverlays = useCallback(() => {
    clearHeatmapData();
    clearPathTraceData();
    scheduleHeatmapRender();
    schedulePathTraceRender();
  }, [clearHeatmapData, clearPathTraceData, scheduleHeatmapRender, schedulePathTraceRender]);

  // Draw cropped square from video
  const drawCropped = (video, ctx, canvasWidth, canvasHeight) => {
    const vWidth = video.videoWidth;
    const vHeight = video.videoHeight;
    const size = Math.min(vWidth, vHeight);
    const sx = (vWidth - size) / 2;
    const sy = (vHeight - size) / 2;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, canvasWidth, canvasHeight);
  };

  // Physics simulation loop
  const runPhysics = useCallback(() => {
    if (labelPhysicsRef.current.length === 0 || stageSize === 0) {
      physicsRequestRef.current = requestAnimationFrame(runPhysics);
      return;
    }
    const states = labelPhysicsRef.current;
    const ds = dragStateRef.current;

    // Soft repulsion between labels
    for (let i = 0; i < states.length; i++) {
      for (let j = i + 1; j < states.length; j++) {
        const a = states[i]; const b = states[j];
        const dx = a.x - b.x; const dy = a.y - b.y;
        const minW = (a.w + b.w) / 2 + SOFT_MARGIN_X;
        const minH = (a.h + b.h) / 2 + SOFT_MARGIN_Y;
        const normDx = Math.abs(dx) / minW;
        const normDy = Math.abs(dy) / minH;
        if (normDx < 1 && normDy < 1) {
          const forceX = Math.pow(1 - normDx, 2) * SOFT_REPULSION_STRENGTH;
          const forceY = Math.pow(1 - normDy, 2) * SOFT_REPULSION_STRENGTH;
          const dirX = dx >= 0 ? 1 : -1;
          const dirY = dy >= 0 ? 1 : -1;
          if (!(ds && ds.index === i)) { a.vx += dirX * forceX; a.vy += dirY * forceY; }
          if (!(ds && ds.index === j)) { b.vx -= dirX * forceX; b.vy -= dirY * forceY; }
        }
      }
    }

    // Update positions
    for (let i = 0; i < states.length; i++) {
      const lp = states[i];
      const targetPoint = trackingPointsRef.current[i]?.point;
      if (!targetPoint) continue;
      const ax = (targetPoint[1] * (stageSize / 1000));
      const ay = (targetPoint[0] * (stageSize / 1000));

      if (ds && ds.index === i) {
        lp.relX = (mousePosRef.current.x - ds.offsetX) - ax;
        lp.relY = (mousePosRef.current.y - ds.offsetY) - ay;
        lp.vx = 0; lp.vy = 0;
      } else {
        lp.vx *= FRICTION; lp.vy *= FRICTION;
        lp.relX += lp.vx; lp.relY += lp.vy;
      }
      lp.x = ax + lp.relX; lp.y = ay + lp.relY;

      // Minimum distance from anchor point
      const curDx = lp.x - ax;
      const curDy = lp.y - ay;
      const curDist = Math.sqrt(curDx * curDx + curDy * curDy) || 0.0001;
      const dirX = curDx / curDist;
      const dirY = curDy / curDist;

      const distToEdge = getDistToRectEdge(dirX, dirY, lp.w, lp.h);
      const minRequiredDist = distToEdge + POINT_SAFETY_BUFFER;

      if (curDist < minRequiredDist && !(ds && ds.index === i)) {
        lp.x = ax + dirX * minRequiredDist;
        lp.y = ay + dirY * minRequiredDist;
        lp.relX = lp.x - ax;
        lp.relY = lp.y - ay;
        lp.vx *= 0.3; lp.vy *= 0.3;
      }
    }

    // Hard collision resolution
    for (let pass = 0; pass < 3; pass++) {
      for (let i = 0; i < states.length; i++) {
        for (let j = i + 1; j < states.length; j++) {
          const a = states[i]; const b = states[j];
          const dx = a.x - b.x; const dy = a.y - b.y;
          const minWidth = (a.w + b.w) / 2 + COLLISION_MARGIN_X;
          const minHeight = (a.h + b.h) / 2 + COLLISION_MARGIN_Y;
          const overlapX = minWidth - Math.abs(dx);
          const overlapY = minHeight - Math.abs(dy);
          if (overlapX > 0 && overlapY > 0) {
            if (overlapX < overlapY) {
              const push = overlapX * 0.5; const side = dx > 0 ? 1 : -1;
              const bA = (ds && ds.index === i) ? 0 : 0.5;
              const bB = (ds && ds.index === j) ? 0 : 0.5;
              const sB = bA + bB || 1;
              const dA = side * push * (bA / sB); const dB = -side * push * (bB / sB);
              a.x += dA; a.relX += dA; b.x += dB; b.relX += dB;
              if (bA > 0) a.vx += dA * 0.1; if (bB > 0) b.vx += dB * 0.1;
            } else {
              const push = overlapY * 0.5; const side = dy > 0 ? 1 : -1;
              const bA = (ds && ds.index === i) ? 0 : 0.5;
              const bB = (ds && ds.index === j) ? 0 : 0.5;
              const sB = bA + bB || 1;
              const dA = side * push * (bA / sB); const dB = -side * push * (bB / sB);
              a.y += dA; a.relY += dA; b.y += dB; b.relY += dB;
              if (bA > 0) a.vy += dA * 0.1; if (bB > 0) b.vy += dB * 0.1;
            }
          }
        }
      }
    }

    // Boundary constraints
    const margin = 12;
    for (let i = 0; i < states.length; i++) {
      const lp = states[i];
      const targetPoint = trackingPointsRef.current[i]?.point;
      if (!targetPoint) continue;
      const ax = (targetPoint[1] * (stageSize / 1000));
      const ay = (targetPoint[0] * (stageSize / 1000));
      const lB = lp.w / 2 + margin; const rB = stageSize - lp.w / 2 - margin;
      const tB = lp.h / 2 + margin; const bB = stageSize - lp.h / 2 - margin;
      if (lp.x < lB) { lp.x = lB; lp.relX = lp.x - ax; lp.vx *= -0.2; }
      if (lp.x > rB) { lp.x = rB; lp.relX = lp.x - ax; lp.vx *= -0.2; }
      if (lp.y < tB) { lp.y = tB; lp.relY = lp.y - ay; lp.vy *= -0.2; }
      if (lp.y > bB) { lp.y = bB; lp.relY = lp.y - ay; lp.vy *= -0.2; }
    }

    const now = performance.now();
    const isDragging = Boolean(ds);
    if (isDragging || now - lastLabelCommitTsRef.current >= OVERLAY_UI_COMMIT_MS) {
      setLabelPositions(states.map(s => ({ x: s.x, y: s.y })));
      lastLabelCommitTsRef.current = now;
    }
    physicsRequestRef.current = requestAnimationFrame(runPhysics);
  }, [stageSize]);

  // Start physics loop
  useEffect(() => {
    physicsRequestRef.current = requestAnimationFrame(runPhysics);
    return () => { if (physicsRequestRef.current) cancelAnimationFrame(physicsRequestRef.current); };
  }, [runPhysics]);

  // Pointer move handler
  const handlePointerMove = (e) => {
    const rect = stageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    mousePosRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    if (dragStateRef.current) {
      const ds = dragStateRef.current;
      const now = Date.now();
      if (now - ds.lastTime > 16) { ds.lastX = e.clientX; ds.lastY = e.clientY; ds.lastTime = now; }
    }
  };

  // Enhanced OpenCV tracking loop with forward-backward validation
  const track = useCallback(() => {
    if (!videoRef.current || !trackingCanvasRef.current || trackingPointsRef.current.length === 0 || !isOpenCvReady) {
      if (videoSrc) trackRequestRef.current = requestAnimationFrame(track);
      return;
    }
    const cv = window.cv;
    const video = videoRef.current;

    // Handle video loop/seek - reset tracking
    if (video.currentTime < lastTimeRef.current - 0.1) {
      resetToOriginal();
      lastTimeRef.current = video.currentTime;
      trackingFrameCountRef.current = 0;
      trackRequestRef.current = requestAnimationFrame(track);
      return;
    }

    lastTimeRef.current = video.currentTime;
    const canvas = trackingCanvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx || video.videoWidth === 0) {
      trackRequestRef.current = requestAnimationFrame(track);
      return;
    }

    drawCropped(video, ctx, TRACKING_RESOLUTION, TRACKING_RESOLUTION);

    try {
      const nextGray = new cv.Mat();
      const currentFrame = cv.imread(canvas);
      cv.cvtColor(currentFrame, nextGray, cv.COLOR_RGBA2GRAY);
      currentFrame.delete();

      if (cvPrevGrayRef.current) {
        // Enhanced Lucas-Kanade parameters
        const nextPts = new cv.Mat();
        const status = new cv.Mat();
        const err = new cv.Mat();
        const winSize = new cv.Size(LK_WIN_SIZE, LK_WIN_SIZE);
        const maxLevel = LK_MAX_LEVEL;
        const criteria = new cv.TermCriteria(
          cv.TERM_CRITERIA_EPS | cv.TERM_CRITERIA_COUNT,
          LK_MAX_ITER,
          LK_EPSILON
        );

        // Forward tracking: prev -> next
        cv.calcOpticalFlowPyrLK(
          cvPrevGrayRef.current, nextGray,
          cvPrevPtsRef.current, nextPts,
          status, err, winSize, maxLevel, criteria
        );

        // Forward-backward validation: track back from next to prev
        const backPts = new cv.Mat();
        const backStatus = new cv.Mat();
        const backErr = new cv.Mat();
        cv.calcOpticalFlowPyrLK(
          nextGray, cvPrevGrayRef.current,
          nextPts, backPts,
          backStatus, backErr, winSize, maxLevel, criteria
        );

        const updatedPoints = [...trackingPointsRef.current];
        const numPoints = updatedPoints.length;
        let driftCount = 0;
        const trackTimestamp = performance.now() / 1000; // seconds for One Euro Filter

        for (let i = 0; i < numPoints; i++) {
          // Handle re-detection blending — lerp from old to new over several frames
          const blend = redetectBlendRef.current[i];
          if (blend && blend.frame < blend.total) {
            const t = blend.frame / blend.total;
            const blendedY = blend.from[0] + (blend.to[0] - blend.from[0]) * t;
            const blendedX = blend.from[1] + (blend.to[1] - blend.from[1]) * t;
            updatedPoints[i] = { ...updatedPoints[i], point: [blendedY, blendedX] };
            blend.frame++;
            lastGoodPositionsRef.current[i] = [blendedY, blendedX];
            smoothedPositionsRef.current[i] = [blendedY, blendedX];
            continue;
          }

          // Check forward tracking succeeded
          if (status.data8S[i] !== 1) {
            // Forward tracking failed - keep last good position
            if (lastGoodPositionsRef.current[i]) {
              updatedPoints[i] = {
                ...updatedPoints[i],
                point: [...lastGoodPositionsRef.current[i]]
              };
            }
            driftCount++;
            continue;
          }

          const nx = nextPts.data32F[i * 2];
          const ny = nextPts.data32F[i * 2 + 1];

          // Calculate forward-backward error
          const origX = cvPrevPtsRef.current.data32F[i * 2];
          const origY = cvPrevPtsRef.current.data32F[i * 2 + 1];
          const backX = backPts.data32F[i * 2];
          const backY = backPts.data32F[i * 2 + 1];
          const fbError = Math.sqrt(
            Math.pow(origX - backX, 2) + Math.pow(origY - backY, 2)
          );

          // Validate tracking quality (tightened threshold: 1.5px)
          if (fbError > FB_ERROR_THRESHOLD || backStatus.data8S[i] !== 1) {
            // Poor tracking - interpolate from last good position rather than freezing
            if (lastGoodPositionsRef.current[i]) {
              updatedPoints[i] = {
                ...updatedPoints[i],
                point: [...lastGoodPositionsRef.current[i]]
              };
            }
            driftCount++;
            continue;
          }

          // Calculate velocity and clamp if too large
          const prevX = updatedPoints[i].point[1] * (TRACKING_RESOLUTION / 1000);
          const prevY = updatedPoints[i].point[0] * (TRACKING_RESOLUTION / 1000);
          const velocity = Math.sqrt(Math.pow(nx - prevX, 2) + Math.pow(ny - prevY, 2));

          if (velocity > MAX_POINT_VELOCITY) {
            // Point jumped too far - likely tracking error
            if (lastGoodPositionsRef.current[i]) {
              updatedPoints[i] = {
                ...updatedPoints[i],
                point: [...lastGoodPositionsRef.current[i]]
              };
            }
            driftCount++;
            continue;
          }

          // Convert to 0-1000 space
          let rawY = ny * (1000 / TRACKING_RESOLUTION);
          let rawX = nx * (1000 / TRACKING_RESOLUTION);

          // Apply One Euro Filter for adaptive smoothing (replaces fixed EMA)
          if (oneEuroFiltersRef.current[i]) {
            rawY = oneEuroFiltersRef.current[i].y.apply(rawY, trackTimestamp);
            rawX = oneEuroFiltersRef.current[i].x.apply(rawX, trackTimestamp);
          }

          // Update point with filtered position
          updatedPoints[i] = {
            ...updatedPoints[i],
            point: [rawY, rawX]
          };

          // Store as last good position and smoothed
          lastGoodPositionsRef.current[i] = [rawY, rawX];
          smoothedPositionsRef.current[i] = [rawY, rawX];
        }

        // --- Skeletal distance constraints (Fix 3c) ---
        // Enforce anatomical plausibility: connected pairs must maintain ~initial distance
        if (initialDistancesRef.current && initialDistancesRef.current.size > 0) {
          const labelIndexMap = {};
          for (let i = 0; i < updatedPoints.length; i++) {
            const label = updatedPoints[i].label?.toLowerCase();
            if (label) labelIndexMap[label] = i;
          }
          for (const [pairKey, initDist] of initialDistancesRef.current) {
            const [labelA, labelB] = pairKey.split('|');
            const idxA = labelIndexMap[labelA];
            const idxB = labelIndexMap[labelB];
            if (idxA === undefined || idxB === undefined) continue;
            const ptA = updatedPoints[idxA].point;
            const ptB = updatedPoints[idxB].point;
            const dy = ptB[0] - ptA[0];
            const dx = ptB[1] - ptA[1];
            const currDist = Math.sqrt(dy * dy + dx * dx);
            if (currDist < 0.001) continue;
            const deviation = Math.abs(currDist - initDist) / initDist;
            if (deviation > SKELETAL_DEVIATION_THRESHOLD) {
              // Pull both points toward valid distance (50/50 correction)
              const targetDist = initDist;
              const scale = targetDist / currDist;
              const midY = (ptA[0] + ptB[0]) / 2;
              const midX = (ptA[1] + ptB[1]) / 2;
              const halfDy = (dy * scale) / 2;
              const halfDx = (dx * scale) / 2;
              // Blend correction: 30% toward corrected position per frame
              const corrA = [midY - halfDy, midX - halfDx];
              const corrB = [midY + halfDy, midX + halfDx];
              updatedPoints[idxA].point = [
                ptA[0] * 0.7 + corrA[0] * 0.3,
                ptA[1] * 0.7 + corrA[1] * 0.3,
              ];
              updatedPoints[idxB].point = [
                ptB[0] * 0.7 + corrB[0] * 0.3,
                ptB[1] * 0.7 + corrB[1] * 0.3,
              ];
              lastGoodPositionsRef.current[idxA] = [...updatedPoints[idxA].point];
              lastGoodPositionsRef.current[idxB] = [...updatedPoints[idxB].point];
              smoothedPositionsRef.current[idxA] = [...updatedPoints[idxA].point];
              smoothedPositionsRef.current[idxB] = [...updatedPoints[idxB].point];
            }
          }
        }

        // --- Sub-frame interpolation: store prev/curr for lerping in render loop ---
        const nowMs = performance.now();
        const elapsed = nowMs - lastTrackTimeRef.current;
        if (lastTrackTimeRef.current > 0 && elapsed > 0) {
          trackIntervalRef.current = elapsed; // update estimated tracking interval
        }
        prevPositionsRef.current = currPositionsRef.current.length > 0
          ? currPositionsRef.current.map(p => [...p])
          : updatedPoints.map(obj => [...obj.point]);
        currPositionsRef.current = updatedPoints.map(obj => [...obj.point]);
        lastTrackTimeRef.current = nowMs;

        trackingPointsRef.current = updatedPoints;
        const uiNow = performance.now();
        if (uiNow - lastDetectedCommitTsRef.current >= OVERLAY_UI_COMMIT_MS) {
          // Sub-frame interpolation: lerp between prev and curr based on elapsed time
          const interpT = Math.min(1, Math.max(0, (uiNow - lastTrackTimeRef.current) / trackIntervalRef.current));
          const interpolatedPoints = updatedPoints.map((obj, idx) => {
            const prev = prevPositionsRef.current[idx];
            const curr = currPositionsRef.current[idx];
            if (!prev || !curr) return obj;
            return {
              ...obj,
              point: [
                prev[0] + (curr[0] - prev[0]) * interpT,
                prev[1] + (curr[1] - prev[1]) * interpT,
              ]
            };
          });
          setDetectedObjects(interpolatedPoints);
          lastDetectedCommitTsRef.current = uiNow;
        }

        // Update max displacement from original positions (for static node filtering)
        for (let pi = 0; pi < updatedPoints.length; pi++) {
          const orig = originalObjectsRef.current[pi]?.point;
          if (!orig) continue;
          const dy = updatedPoints[pi].point[0] - orig[0];
          const dx = updatedPoints[pi].point[1] - orig[1];
          const dist = Math.sqrt(dy * dy + dx * dx);
          if (dist > (maxDisplacementRef.current[pi] || 0)) {
            maxDisplacementRef.current[pi] = dist;
          }
        }

        // Accumulate path history for path trace layer
        if (isPathTraceEnabledRef.current) {
          for (let pi = 0; pi < updatedPoints.length; pi++) {
            if (!pathHistoryRef.current[pi]) pathHistoryRef.current[pi] = [];
            pathHistoryRef.current[pi].push([...updatedPoints[pi].point]);
            if (pathHistoryRef.current[pi].length > PATH_HISTORY_MAX_LENGTH) {
              pathHistoryRef.current[pi].shift();
            }
          }
          pathTraceDirtyRef.current = true;
          schedulePathTraceRender();
        }

        // Heatmap accumulation — spread tracked points into grid
        if (isHeatmapEnabledRef.current) {
          const grid = heatmapGridRef.current;
          const activeLandmarks = selectedLandmarksRef.current;
          const filterBySelection = activeLandmarks.size > 0;
          for (let pi = 0; pi < updatedPoints.length; pi++) {
            if (filterBySelection && !activeLandmarks.has(pi)) continue;
            const pt = updatedPoints[pi].point;
            // pt is [y, x] in 0-1000 space → normalize to grid
            const gx = Math.floor((pt[1] / 1000) * HEATMAP_GRID_SIZE);
            const gy = Math.floor((pt[0] / 1000) * HEATMAP_GRID_SIZE);
            // Gaussian spread
            for (let dy = -HEATMAP_GAUSSIAN_RADIUS; dy <= HEATMAP_GAUSSIAN_RADIUS; dy++) {
              for (let dx = -HEATMAP_GAUSSIAN_RADIUS; dx <= HEATMAP_GAUSSIAN_RADIUS; dx++) {
                const hnx = gx + dx, hny = gy + dy;
                if (hnx < 0 || hnx >= HEATMAP_GRID_SIZE || hny < 0 || hny >= HEATMAP_GRID_SIZE) continue;
                const dist2 = dx * dx + dy * dy;
                const weight = Math.exp(-dist2 / (2 * HEATMAP_GAUSSIAN_SIGMA * HEATMAP_GAUSSIAN_SIGMA));
                grid[hny * HEATMAP_GRID_SIZE + hnx] += weight;
              }
            }
          }
          heatmapSampleCountRef.current++;
          heatmapDirtyRef.current = true;
          scheduleHeatmapRender();
        }

        // Clean up matrices
        backPts.delete();
        backStatus.delete();
        backErr.delete();
        cvPrevGrayRef.current.delete();
        cvPrevPtsRef.current.delete();
        cvPrevGrayRef.current = nextGray;
        cvPrevPtsRef.current = nextPts;
        status.delete();
        err.delete();

        // Increment frame counter
        trackingFrameCountRef.current++;

        // Auto re-detect when too many points are drifting or periodic interval reached
        const shouldRedetect = driftCount > numPoints * 0.4 ||
          trackingFrameCountRef.current % DRIFT_REDETECT_FRAMES === 0;
        if (shouldRedetect && trackingFrameCountRef.current > 10) {
          // Smooth re-detection blending: lerp from old position to new over REDETECT_BLEND_FRAMES
          // instead of hard-snapping positions
          const reinitPts = updatedPoints.flatMap(obj => [
            obj.point[1] * (TRACKING_RESOLUTION / 1000),
            obj.point[0] * (TRACKING_RESOLUTION / 1000)
          ]);
          cvPrevPtsRef.current.delete();
          cvPrevPtsRef.current = cv.matFromArray(reinitPts.length / 2, 1, cv.CV_32FC2, reinitPts);

          // Set up smooth blending for each landmark
          for (let bi = 0; bi < numPoints; bi++) {
            const oldPos = lastGoodPositionsRef.current[bi] || updatedPoints[bi].point;
            const newPos = [...updatedPoints[bi].point];
            // Only blend if positions differ noticeably (> 5 units in 0-1000 space)
            const blendDy = newPos[0] - oldPos[0];
            const blendDx = newPos[1] - oldPos[1];
            const blendDist = Math.sqrt(blendDy * blendDy + blendDx * blendDx);
            if (blendDist > 5) {
              redetectBlendRef.current[bi] = {
                from: [...oldPos],
                to: newPos,
                frame: 0,
                total: REDETECT_BLEND_FRAMES,
              };
            }
          }

          lastGoodPositionsRef.current = updatedPoints.map(obj => [...obj.point]);
          smoothedPositionsRef.current = updatedPoints.map(obj => [...obj.point]);
        }

      } else {
        // Initialize tracking on first frame
        cvPrevGrayRef.current = nextGray;
        const ptsArray = trackingPointsRef.current.flatMap(obj => [
          obj.point[1] * (TRACKING_RESOLUTION / 1000),
          obj.point[0] * (TRACKING_RESOLUTION / 1000)
        ]);
        cvPrevPtsRef.current = cv.matFromArray(ptsArray.length / 2, 1, cv.CV_32FC2, ptsArray);

        // Initialize last good positions and smoothed positions
        lastGoodPositionsRef.current = trackingPointsRef.current.map(obj => [...obj.point]);
        smoothedPositionsRef.current = trackingPointsRef.current.map(obj => [...obj.point]);
        trackingFrameCountRef.current = 0;

        // Initialize One Euro Filters — one per landmark, per axis
        oneEuroFiltersRef.current = trackingPointsRef.current.map(() => ({
          y: new OneEuroFilter(0.5, 0.007, 1.0),
          x: new OneEuroFilter(0.5, 0.007, 1.0),
        }));

        // Initialize sub-frame interpolation positions
        prevPositionsRef.current = trackingPointsRef.current.map(obj => [...obj.point]);
        currPositionsRef.current = trackingPointsRef.current.map(obj => [...obj.point]);
        lastTrackTimeRef.current = performance.now();

        // Initialize re-detection blend state
        redetectBlendRef.current = trackingPointsRef.current.map(() => null);

        // Initialize skeletal distance constraints from first detection
        const labelIndexMap = {};
        for (let i = 0; i < trackingPointsRef.current.length; i++) {
          const label = trackingPointsRef.current[i].label?.toLowerCase();
          if (label) labelIndexMap[label] = i;
        }
        const distMap = new Map();
        for (const [labelA, labelB] of SKELETAL_CONSTRAINT_PAIRS) {
          const idxA = labelIndexMap[labelA.toLowerCase()];
          const idxB = labelIndexMap[labelB.toLowerCase()];
          if (idxA === undefined || idxB === undefined) continue;
          const ptA = trackingPointsRef.current[idxA].point;
          const ptB = trackingPointsRef.current[idxB].point;
          const dy = ptB[0] - ptA[0];
          const dx = ptB[1] - ptA[1];
          const dist = Math.sqrt(dy * dy + dx * dx);
          distMap.set(`${labelA.toLowerCase()}|${labelB.toLowerCase()}`, dist);
        }
        initialDistancesRef.current = distMap;
      }
    } catch (e) {
      console.warn("[SlamMobile] OpenCV Tracking Error:", e);
    }

    trackRequestRef.current = requestAnimationFrame(track);
  }, [videoSrc, resetToOriginal, isOpenCvReady, scheduleHeatmapRender, schedulePathTraceRender]);

  // Start/stop tracking based on play state
  useEffect(() => {
    if (isPlaying && hasAnalyzed) {
      trackRequestRef.current = requestAnimationFrame(track);
    } else if (trackRequestRef.current) {
      cancelAnimationFrame(trackRequestRef.current);
    }
    return () => { if (trackRequestRef.current) cancelAnimationFrame(trackRequestRef.current); };
  }, [isPlaying, hasAnalyzed, track]);

  // Generate analysis summary - MUST be defined before captureAndAnalyze
  const generateSummary = useCallback((objects) => {
    if (!objects || objects.length === 0) return null;

    const sportConfig = SPORT_CONFIGS[selectedSport] || SPORT_CONFIGS.general;
    const avgConfidence = objects.reduce((sum, obj) => sum + (getConfidence(obj)), 0) / objects.length;
    const highConfObjects = objects.filter(obj => (getConfidence(obj)) >= 0.75);
    const lowConfObjects = objects.filter(obj => (getConfidence(obj)) < 0.5);

    return {
      totalLandmarks: objects.length,
      avgConfidence: Math.round(avgConfidence * 100),
      highConfCount: highConfObjects.length,
      lowConfCount: lowConfObjects.length,
      sport: sportConfig.name,
      customFocus: userPrompt.trim() || null,
      focusAreas: sportConfig.focusAreas,
      topFindings: highConfObjects.slice(0, 3).map(obj => ({
        label: obj.label,
        description: obj.description,
        confidence: Math.round((getConfidence(obj)) * 100)
      })),
      warnings: lowConfObjects.map(obj => obj.label)
    };
  }, [selectedSport, userPrompt]);

  // Analyze frame using Gemini
  const captureAndAnalyze = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;

    setHasAnalyzed(false);
    setIsPlaying(false);
    video.pause();
    setDetectedObjects([]);
    setVisibleLabelIndices(new Set());
    setTypedIndices(new Set());
    setDotTransitionStartTimes([]);
    setSelectedLandmarks(new Set());
    setAnalysisError(null);
    setIsAnalyzing(true);
    clearHeatmapData();

    // Auto-enable heatmap if prompt implies spatial analysis
    if (promptInfersHeatmap(userPrompt)) setIsHeatmapEnabled(true);

    video.currentTime = 0;
    lastTimeRef.current = 0;

    await new Promise((resolve) => {
      const onSeeked = () => { video.removeEventListener('seeked', onSeeked); resolve(); };
      video.addEventListener('seeked', onSeeked);
      setTimeout(resolve, 500);
    });

    const context = canvasRef.current.getContext('2d', { willReadFrequently: true });
    if (context && video.videoWidth > 0) {
      canvasRef.current.width = 1000;
      canvasRef.current.height = 1000;
      drawCropped(video, context, 1000, 1000);

      const base64Image = canvasRef.current.toDataURL('image/jpeg', 0.85).split(',')[1];
      setLastFrameBase64(base64Image); // Save for chat follow-up

      try {
        // Build prompt with user input if provided
        const basePrompt = `Analyze this ${sportConfig.name} video frame for player development. Focus on: ${sportConfig.focusAreas.join(', ')}. Identify key anatomical landmarks, joint positions, and technique indicators for coaching feedback.`;
        const fullPrompt = userPrompt.trim()
          ? `${basePrompt}\n\nADDITIONAL FOCUS: ${userPrompt.trim()}`
          : basePrompt;

        // Call backend API for Gemini biomechanics analysis
        const response = await fetch(`${API_BASE}/api/video/analyze-frame`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image: base64Image,
            sport: selectedSport,
            prompt: fullPrompt
          })
        });

        if (!response.ok) {
          throw new Error(`Analysis failed: ${response.statusText}`);
        }

        const data = await response.json();
        const results = data.objects || [];

        if (results.length === 0) {
          setAnalysisError('No biomechanical landmarks detected. Try with a clearer video showing the athlete.');
          setIsAnalyzing(false);
          return;
        }

        // Calculate colors from frame
        const colors = results.map(obj => getLocalAverageColor(context, obj.point[1], obj.point[0]));
        setObjectColors(colors);
        const keylines = colors.map(c => modifyColor(c, 0.35, 0.20));
        setKeylineColors(keylines);

        // Store original positions
        originalObjectsRef.current = JSON.parse(JSON.stringify(results));

        // Initialize physics
        const centroid = calculateCentroid(results);
        labelPhysicsRef.current = results.map(obj => {
          const tL = (obj.label?.length || 5) + (obj.description?.length || 20) + 2;
          const eW = Math.min(tL * 7 + 20, 280);
          const eH = Math.max(LABEL_MIN_HEIGHT, 50);
          const off = getDynamicOffset(obj.point, centroid, stageSize, eW, eH);
          const ax = obj.point[1] * (stageSize / 1000);
          const ay = obj.point[0] * (stageSize / 1000);
          return { x: ax + off.relX, y: ay + off.relY, relX: off.relX, relY: off.relY, vx: 0, vy: 0, w: eW, h: eH };
        });

        setDetectedObjects(results);
        trackingPointsRef.current = results;
        // Initialize enhanced tracking state
        lastGoodPositionsRef.current = results.map(obj => [...obj.point]);
        smoothedPositionsRef.current = results.map(obj => [...obj.point]);
        trackingFrameCountRef.current = 0;
        // Initialize One Euro Filters for adaptive smoothing
        oneEuroFiltersRef.current = results.map(() => ({
          y: new OneEuroFilter(0.5, 0.007, 1.0),
          x: new OneEuroFilter(0.5, 0.007, 1.0),
        }));
        // Initialize sub-frame interpolation
        prevPositionsRef.current = results.map(obj => [...obj.point]);
        currPositionsRef.current = results.map(obj => [...obj.point]);
        lastTrackTimeRef.current = performance.now();
        // Initialize re-detection blend
        redetectBlendRef.current = results.map(() => null);
        // Skeletal constraints will be computed on first tracking frame
        initialDistancesRef.current = null;
        // Initialize displacement + path trace tracking
        maxDisplacementRef.current = new Array(results.length).fill(0);
        pathHistoryRef.current = results.map(() => []);
        pathTraceDirtyRef.current = true;
        setHasAnalyzed(true);
        setResetKey(prev => prev + 1);
        clearOpenCvMemory();

        // Generate analysis summary
        const summary = generateSummary(results);
        setAnalysisSummary(summary);

        // Auto-play after analysis
        video.currentTime = 0;
        video.play();
        setIsPlaying(true);

      } catch (error) {
        console.error('[SlamMobile] Analysis error:', error);
        setAnalysisError(error.message || 'Failed to analyze frame');
      } finally {
        setIsAnalyzing(false);
      }
    }
  }, [stageSize, clearOpenCvMemory, clearHeatmapData, selectedSport, sportConfig, userPrompt, generateSummary, setIsHeatmapEnabled]);

  // Drag handlers
  const handlePointerDown = (e, index) => {
    e.preventDefault();
    const rect = stageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const lp = labelPhysicsRef.current[index];
    dragStateRef.current = {
      index,
      offsetX: (e.clientX - rect.left) - lp.x,
      offsetY: (e.clientY - rect.top) - lp.y,
      lastX: e.clientX,
      lastY: e.clientY,
      lastTime: Date.now()
    };
    e.target.setPointerCapture(e.pointerId);
  };

  const handlePointerUp = (e) => {
    if (dragStateRef.current) {
      const ds = dragStateRef.current;
      const lp = labelPhysicsRef.current[ds.index];
      const now = Date.now();
      const dt = Math.max(1, now - ds.lastTime);
      lp.vx = ((e.clientX - ds.lastX) / dt) * 14;
      lp.vy = ((e.clientY - ds.lastY) / dt) * 14;
      dragStateRef.current = null;
      e.target.releasePointerCapture(e.pointerId);
    }
  };

  // Handle time update
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // Handle video load
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setVideoNativeDims({
        width: videoRef.current.videoWidth,
        height: videoRef.current.videoHeight,
      });
      // Leave gaze overlay off by default — user controls it via the Gaze button
    }
  };

  // Overlay keyboard shortcuts for fast film review.
  useEffect(() => {
    const handleOverlayHotkeys = (e) => {
      const target = e.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      if (!hasAnalyzed) return;

      if (e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        setIsHeatmapEnabled((prev) => !prev);
      } else if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        setIsPathTraceEnabled((prev) => !prev);
      } else if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        setIsBatmanMode((prev) => !prev);
      } else if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        setIsMarkersEnabled((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleOverlayHotkeys);
    return () => window.removeEventListener('keydown', handleOverlayHotkeys);
  }, [hasAnalyzed, setIsBatmanMode, setIsHeatmapEnabled, setIsPathTraceEnabled, setIsMarkersEnabled]);

  // Track responsive breakpoint for JS-conditional logic
  useEffect(() => {
    const syncMobile = () => setIsMobile(isMobileViewport());
    window.addEventListener('resize', syncMobile);
    return () => window.removeEventListener('resize', syncMobile);
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = (e) => {
    e?.stopPropagation();
    if (!document.fullscreenElement) {
      stageContainerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Progress percentage
  const progressPercent = useMemo(() => {
    if (duration === 0) return 0;
    return (currentTime / duration) * 100;
  }, [currentTime, duration]);

  // Export report handler
  const handleExportReport = useCallback(() => {
    if (detectedObjects.length === 0) return;

    const avgConfidence = detectedObjects.reduce((sum, obj) => sum + (getConfidence(obj)), 0) / detectedObjects.length;
    const sportConfig = SPORT_CONFIGS[selectedSport] || SPORT_CONFIGS.general;

    // Build markdown report
    const reportContent = `# Biomechanics Analysis Report

**Sport:** ${sportConfig.name}
**Date:** ${new Date().toLocaleDateString()}
**Landmarks Detected:** ${detectedObjects.length}
**Average Confidence:** ${Math.round(avgConfidence * 100)}%
${userPrompt.trim() ? `**Custom Focus:** ${userPrompt.trim()}` : ''}

## Detected Landmarks

${detectedObjects.map((obj, i) => {
  const confidence = getConfidence(obj);
  const confLabel = getConfidenceLabel(confidence);
  return `### ${i + 1}. ${obj.label}
- **Position:** [${obj.point[0].toFixed(0)}, ${obj.point[1].toFixed(0)}]
- **Confidence:** ${Math.round(confidence * 100)}% (${confLabel})
- **Observation:** ${obj.description}
`;
}).join('\n')}

## Focus Areas
${userPrompt.trim() ? `- **${userPrompt.trim()}** (custom)\n` : ''}${sportConfig.focusAreas.map(area => `- ${area}`).join('\n')}

## Summary
This analysis identified ${detectedObjects.length} biomechanical landmarks with an average detection confidence of ${Math.round(avgConfidence * 100)}%.${userPrompt.trim() ? ` Analysis was specifically focused on "${userPrompt.trim()}" per coach request.` : ''}

${detectedObjects.filter(o => getConfidence(o) < 0.5).length > 0 ?
  `**Note:** ${detectedObjects.filter(o => getConfidence(o) < 0.5).length} landmark(s) have low confidence and may require manual verification.` : ''}

---
*Generated by SLAM Sports AI - Biomechanics Analysis System*
`;

    // Create and download file
    const blob = new Blob([reportContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `biomechanics-analysis-${selectedSport}-${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [detectedObjects, selectedSport, userPrompt]);

  return (
    <div className="w-full h-[100dvh] bg-black text-white font-mono flex flex-col overflow-hidden md:h-full md:flex-row">
      {/* Main Content */}
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Compact Header: label + sport dropdown + focus input + Go */}
        <div className="flex-shrink-0 border-b border-zinc-800 px-2.5 py-2">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-1 flex-shrink-0">
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.gold, flexShrink: 0 }} />
              <span style={{ fontSize: 9, fontWeight: 700, color: C.gold, letterSpacing: 2, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                Practice Film
              </span>
            </div>
            <select
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-100 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400 cursor-pointer flex-shrink-0"
            >
              {Object.entries(SPORT_CONFIGS).map(([key, config]) => (
                <option key={key} value={key}>{config.icon} {config.name}</option>
              ))}
            </select>
            <input
              type="text"
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              placeholder="Custom focus..."
              className="flex-1 min-w-0 px-2.5 py-1.5 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-amber-400"
            />
            <button
              onClick={captureAndAnalyze}
              disabled={bundleAnalyzing}
              className="flex-shrink-0 px-3 py-1.5 bg-amber-400 hover:bg-amber-300 disabled:bg-amber-400/50 text-black text-xs font-bold rounded-lg transition-colors"
            >
              {bundleAnalyzing ? 'Analyzing...' : uploadedFiles.length > 1 || uploadedUrls.length > 0 ? 'Analyze All' : 'Go'}
            </button>
          </div>
        </div>

        {/* Stage Container */}
        <div className="flex items-center justify-center bg-zinc-900/30 p-2 flex-1 min-h-0 md:p-4">
          <div
            ref={stageContainerRef}
            onPointerMove={handlePointerMove}
            style={{ maxWidth: isMobile ? '100%' : 'min(90vw, calc(100vh - 200px))', maxHeight: '100%', width: '100%', aspectRatio: videoNativeDims.width && videoNativeDims.height ? `${videoNativeDims.width} / ${videoNativeDims.height}` : '16 / 9' }}
            className="relative bg-black border border-zinc-800 overflow-hidden rounded-lg shadow-2xl"
          >
            {videoSrc ? (
              <>
                {/* Video Layer */}
                <video
                  ref={videoRef}
                  src={videoSrc}
                  className="w-full h-full object-contain"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  muted={isMuted}
                  loop
                  playsInline
                />

                {/* Heatmap Overlay Canvas */}
                <canvas
                  ref={heatmapCanvasRef}
                  className="absolute inset-0 w-full h-full pointer-events-none z-10"
                  style={{
                    opacity: isHeatmapEnabled ? 1 : 0,
                    transition: 'opacity 0.25s ease-out',
                    mixBlendMode: 'screen',
                    filter: isHeatmapEnabled ? 'saturate(1.15) blur(0.5px)' : 'none',
                  }}
                />
                {isHeatmapEnabled && hasAnalyzed && (
                  <div className="absolute top-2 left-2 z-20 max-w-[200px]">
                    {/* Heatmap Badge + Legend */}
                    <div className="bg-black/85 backdrop-blur-sm rounded-lg border border-orange-500/30 overflow-hidden">
                      <div className="px-2 py-1 bg-orange-500/20 border-b border-orange-500/20">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-orange-400">
                          Movement Heatmap
                        </span>
                      </div>
                      <div className="px-2 py-1.5 space-y-1">
                        <p className="text-[8px] text-zinc-400 leading-tight">
                          Anatomical landmark dwell time — shows where joints spend the most time during the motion.
                        </p>
                        {/* Color scale */}
                        <div className="flex items-center gap-1">
                          <div className="flex-1 h-1.5 rounded-full" style={{
                            background: 'linear-gradient(to right, rgba(0,0,255,0.7), rgba(0,255,255,0.7), rgba(0,255,0,0.7), rgba(255,255,0,0.7), rgba(255,0,0,0.7))'
                          }} />
                        </div>
                        <div className="flex justify-between text-[7px] text-zinc-500">
                          <span>Low</span>
                          <span>High</span>
                        </div>
                        <p className="text-[7px] text-zinc-500 leading-tight">
                          Red zones = repetitive stress areas. Use for injury prevention and technique consistency.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Path Trace Overlay Canvas */}
                <canvas
                  ref={pathTraceCanvasRef}
                  className="absolute inset-0 w-full h-full pointer-events-none z-15"
                  style={{
                    opacity: isPathTraceEnabled ? 1 : 0,
                    transition: 'opacity 0.2s ease-out',
                    mixBlendMode: 'lighter',
                  }}
                />
                {isPathTraceEnabled && hasAnalyzed && (
                  <div className="absolute top-2 z-20 bg-emerald-500/80 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                    style={{ left: isHeatmapEnabled ? '5.5rem' : '0.5rem' }}
                  >
                    Path Trace
                  </div>
                )}

                {/* Gaze Tracking Overlay (MediaPipe PoseLandmarker, multi-player) */}
                <GazeOverlayV2
                  videoEl={videoRef.current}
                  width={stageSize}
                  height={stageSize}
                  visible={isGazeEnabled}
                  sport={selectedSport}
                  numPoses={10}
                />

                {/* Analysis Button (shown when not analyzed) */}
                {!hasAnalyzed && !isAnalyzing && !analysisError && (
                  <div className="absolute inset-0 flex items-center justify-center z-50 bg-black/50">
                    <button
                      onClick={captureAndAnalyze}
                      disabled={!isOpenCvReady}
                      className="relative w-[84px] h-[84px] md:w-[100px] md:h-[100px] border-2 border-amber-400 rounded-full flex flex-col items-center justify-center gap-2 hover:scale-110 active:scale-95 cursor-pointer transition-all duration-300"
                      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
                    >
                      <Activity className="w-6 h-6 text-amber-400" />
                      <span className="text-[10px] text-amber-400 uppercase tracking-wider">Analyze</span>
                    </button>
                    {!isOpenCvReady && (
                      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-xs text-amber-400/60 bg-black/80 px-3 py-1 rounded">
                        Loading OpenCV...
                      </div>
                    )}
                  </div>
                )}

                {/* Full-screen analysis animation — shown while Gemini processes */}
                {isAnalyzing && (
                  <div className="absolute inset-0 z-50 bg-black/85 flex flex-col items-center justify-center overflow-hidden">
                    {/* CSS animations injected inline */}
                    <style>{`
                      @keyframes slam-scan-line {
                        0%   { top: 0%; opacity: 1; }
                        45%  { top: 100%; opacity: 1; }
                        50%  { top: 100%; opacity: 0; }
                        51%  { top: 0%; opacity: 0; }
                        55%  { top: 0%; opacity: 1; }
                        100% { top: 0%; opacity: 1; }
                      }
                      @keyframes slam-ring-pulse {
                        0%, 100% { transform: scale(1);   opacity: 0.6; }
                        50%      { transform: scale(1.08); opacity: 1; }
                      }
                      @keyframes slam-corner-flash {
                        0%, 100% { opacity: 0.4; }
                        50%      { opacity: 1; }
                      }
                    `}</style>

                    {/* Corner brackets */}
                    <div className="absolute top-3 left-3 w-7 h-7 border-l-2 border-t-2 border-amber-400" style={{ animation: 'slam-corner-flash 1.5s ease-in-out infinite' }} />
                    <div className="absolute top-3 right-3 w-7 h-7 border-r-2 border-t-2 border-amber-400" style={{ animation: 'slam-corner-flash 1.5s ease-in-out infinite 0.375s' }} />
                    <div className="absolute bottom-3 left-3 w-7 h-7 border-l-2 border-b-2 border-amber-400" style={{ animation: 'slam-corner-flash 1.5s ease-in-out infinite 0.75s' }} />
                    <div className="absolute bottom-3 right-3 w-7 h-7 border-r-2 border-b-2 border-amber-400" style={{ animation: 'slam-corner-flash 1.5s ease-in-out infinite 1.125s' }} />

                    {/* Amber scan line */}
                    <div
                      className="absolute left-0 right-0 h-0.5 pointer-events-none"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, #E8B800 30%, #E8B800 70%, transparent 100%)',
                        boxShadow: '0 0 16px 6px rgba(232,184,0,0.55)',
                        animation: 'slam-scan-line 2s ease-in-out infinite',
                      }}
                    />

                    {/* Central status badge */}
                    <div className="flex flex-col items-center gap-3 px-4">
                      {/* Pulsing outer ring */}
                      <div
                        className="w-16 h-16 rounded-full border-2 border-amber-400/60 flex items-center justify-center"
                        style={{ animation: 'slam-ring-pulse 1.4s ease-in-out infinite' }}
                      >
                        <Activity className="w-6 h-6 text-amber-400" />
                      </div>

                      <div className="text-center">
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400">
                          Analyzing biomechanics
                        </p>
                        <p className="text-[9px] text-zinc-400 mt-0.5 tracking-wider">
                          Gemini is scanning landmarks...
                        </p>
                      </div>

                      {/* Animated bar group */}
                      <div className="flex items-end gap-0.5 h-5">
                        {Array.from({ length: 16 }).map((_, i) => (
                          <div
                            key={i}
                            className="w-1 bg-amber-400/60 rounded-sm"
                            style={{
                              height: `${40 + Math.sin(i * 0.7) * 40}%`,
                              animation: `slam-ring-pulse 1.2s ease-in-out ${i * 75}ms infinite`,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Error State */}
                {analysisError && (
                  <div className="absolute inset-0 flex items-center justify-center z-50 bg-black/90">
                    <div className="text-center p-6 max-w-sm">
                      <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
                      <p className="text-white text-sm mb-4">{analysisError}</p>
                      <button
                        onClick={captureAndAnalyze}
                        className="px-4 py-2 bg-amber-400 text-black rounded font-medium text-sm hover:bg-amber-300 transition-colors"
                      >
                        Retry Analysis
                      </button>
                    </div>
                  </div>
                )}

                {/* Analysis Summary Popup - shows briefly after analysis */}
                {analysisSummary && hasAnalyzed && !isAnalyzing && (
                  <div className="absolute bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-300">
                    <div className="bg-zinc-900/95 backdrop-blur-sm border border-zinc-700 rounded-lg p-3 shadow-xl">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-amber-400" />
                          <span className="text-sm font-semibold text-white">Analysis Complete</span>
                        </div>
                        <button
                          onClick={() => setAnalysisSummary(null)}
                          className="text-zinc-400 hover:text-white transition-colors"
                        >
                          <CloseIcon size={14} />
                        </button>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-zinc-400 mb-2">
                        <span>{analysisSummary.sport}</span>
                        <span>{analysisSummary.totalLandmarks} landmarks</span>
                        <span
                          style={{ color: getConfidenceColor(analysisSummary.avgConfidence / 100) }}
                        >
                          {analysisSummary.avgConfidence}% avg confidence
                        </span>
                      </div>
                      {analysisSummary.topFindings.length > 0 && (
                        <div className="space-y-1">
                          <div className="text-[10px] text-zinc-500 uppercase">Top Findings:</div>
                          {analysisSummary.topFindings.map((finding, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ backgroundColor: getConfidenceColor(finding.confidence / 100) }}
                              />
                              <span className="text-white font-medium">{finding.label}</span>
                              <span className="text-zinc-500 truncate flex-1">{finding.description}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {analysisSummary.warnings.length > 0 && (
                        <div className="mt-2 text-[10px] text-amber-400 flex items-center gap-1">
                          <AlertTriangle size={10} />
                          <span>Low confidence: {analysisSummary.warnings.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tracking Overlay - Clean minimal labels with lines (toggled by Markers button) */}
                {isMarkersEnabled && hasAnalyzed && !isAnalyzing && (
                  <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
                    {detectedObjects.map((obj, i) => {
                      // Filter based on selection: if items selected, only show those
                      const hasSelection = selectedLandmarks.size > 0;
                      if (hasSelection && !selectedLandmarks.has(i)) return null;

                      // Skip static nodes — only show nodes that actually moved
                      const displacement = maxDisplacementRef.current[i] || 0;
                      if (displacement < STATIC_NODE_THRESHOLD && trackingFrameCountRef.current > 15) return null;

                      const px = obj.point[1] * (stageSize / 1000);
                      const py = obj.point[0] * (stageSize / 1000);
                      const lPos = labelPositions[i] || { x: px + 50, y: py - 30 };
                      const isVisible = visibleLabelIndices.has(i);
                      const transitionStart = dotTransitionStartTimes[i];

                      const isPointOffScreen = obj.point[0] < 0 || obj.point[0] > 1000 || obj.point[1] < 0 || obj.point[1] > 1000;

                      // Don't render until transition has started
                      if (transitionStart === null) return null;

                      return (
                        <BiomechanicsLabel
                          key={`${resetKey}-${i}`}
                          obj={obj}
                          index={i}
                          isVisible={isVisible && !isPointOffScreen}
                          labelPosition={lPos}
                          pointPosition={obj.point}
                          color={objectColors[i] || '#FFD700'}
                          hasFinishedTyping={typedIndices.has(i)}
                          onTypeComplete={() => setTypedIndices(prev => new Set([...prev, i]))}
                          onPointerDown={(e) => handlePointerDown(e, i)}
                          onPointerUp={handlePointerUp}
                          isBatmanMode={isBatmanMode}
                          sportColor={sportConfig.color}
                          stageSize={stageSize}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              /* Upload Prompt — camera + file picker */
              <div className="flex flex-col items-center justify-center h-full gap-3 px-6">
                <button
                  onClick={() => setShowCamera(true)}
                  className="w-full max-w-[220px] flex items-center gap-3 px-4 py-3 rounded-lg bg-zinc-800/80 hover:bg-zinc-700 transition-colors group"
                >
                  <Camera className="w-6 h-6 text-green-400/70 group-hover:text-green-400 transition-colors" />
                  <span className="text-zinc-300 group-hover:text-white text-sm font-medium">Use Camera</span>
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full max-w-[220px] flex items-center gap-3 px-4 py-3 rounded-lg bg-zinc-800/80 hover:bg-zinc-700 transition-colors group"
                >
                  <Upload className="w-6 h-6 text-amber-400/70 group-hover:text-amber-400 transition-colors" />
                  <span className="text-zinc-300 group-hover:text-white text-sm font-medium">Upload Files</span>
                </button>
                <span className="text-zinc-600 text-[10px] text-center">
                  Video, PDF, CSV, audio, images
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Multi-File Preview Strip */}
        {(uploadedFiles.length > 0 || uploadedUrls.length > 0) && (
          <MultiFilePreview
            files={uploadedFiles}
            urls={uploadedUrls}
            onRemoveFile={handleRemoveFile}
            onRemoveUrl={handleRemoveUrl}
            onAddUrl={handleAddUrl}
            onAddFiles={handleAddFiles}
            onOpenCamera={() => setShowCamera(true)}
          />
        )}

        {/* Controls Bar */}
        <div className="flex-shrink-0 bg-zinc-950 border-t border-zinc-800 px-2.5 py-2 md:px-4 md:py-3">
          {/* Progress Bar */}
          {videoSrc && (
            <div className="w-full h-1 bg-zinc-800 rounded mb-3 overflow-hidden">
              <div
                className="h-full transition-all duration-100"
                style={{ width: `${progressPercent}%`, backgroundColor: sportConfig.color }}
              />
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              {/* Analyze */}
              <button
                onClick={captureAndAnalyze}
                disabled={isAnalyzing || !videoSrc || !isOpenCvReady}
                className="text-zinc-400 hover:text-amber-400 disabled:opacity-30 transition-colors"
                title="Analyze Frame"
              >
                <Activity size={20} />
              </button>

              <div className="w-px h-5 bg-zinc-700" />

              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                disabled={!videoSrc}
                className="text-zinc-400 hover:text-white disabled:opacity-30 transition-colors"
              >
                {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
              </button>

              {/* Restart */}
              <button
                onClick={restart}
                disabled={!videoSrc}
                className="text-zinc-400 hover:text-white disabled:opacity-30 transition-colors"
                title="Reset Tracking"
              >
                <RotateCcw size={18} />
              </button>

              {/* Time display */}
              <div className="text-xs font-mono text-zinc-500">
                <span style={{ color: sportConfig.color }}>{currentTime.toFixed(1)}</span>
                <span className="mx-1">/</span>
                <span>{duration.toFixed(1)}</span>
              </div>
            </div>

            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
              {/* Overlay toggles — labeled pill buttons */}
              <button
                onClick={() => setIsMarkersEnabled(!isMarkersEnabled)}
                disabled={!hasAnalyzed}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all disabled:opacity-20 ${
                  isMarkersEnabled
                    ? 'bg-sky-500/20 text-sky-400 ring-1 ring-sky-500/40'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                }`}
                title="Toggle biomechanics marker nodes (keyboard shortcut: M)"
                aria-label="Toggle biomechanics marker nodes"
              >
                <Crosshair size={14} />
                <span>Markers</span>
                <kbd className={`text-[9px] px-1 rounded font-mono border leading-none py-px transition-all duration-200 ${
                  isMarkersEnabled
                    ? 'bg-sky-400/20 text-sky-300 border-sky-400/40'
                    : 'bg-white/10 text-white/50 border-white/20'
                }`}>M</kbd>
              </button>

              <button
                onClick={() => setIsGazeEnabled(!isGazeEnabled)}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all ${
                  isGazeEnabled
                    ? 'bg-amber-400/20 text-amber-400 ring-1 ring-amber-400/40'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                }`}
                title={gazeProgress || "Toggle eye gaze tracking overlay"}
              >
                <ScanEye size={14} />
                <span>{isGazeLoading ? 'Detecting...' : 'Gaze'}</span>
              </button>

              <button
                onClick={() => setIsHeatmapEnabled(!isHeatmapEnabled)}
                disabled={!hasAnalyzed}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all disabled:opacity-20 ${
                  isHeatmapEnabled
                    ? 'bg-orange-500/20 text-orange-400 ring-1 ring-orange-500/40'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                }`}
                title="Movement Heatmap — shows where joints dwell most during motion. Red = repetitive stress zones for injury prevention."
              >
                <Flame size={14} />
                <span>Heat</span>
              </button>

              <button
                onClick={() => setIsPathTraceEnabled(!isPathTraceEnabled)}
                disabled={!hasAnalyzed}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all disabled:opacity-20 ${
                  isPathTraceEnabled
                    ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                }`}
                title="Toggle path trace overlay"
              >
                <Route size={14} />
                <span>Path</span>
              </button>

              <button
                onClick={() => setIsBatmanMode(!isBatmanMode)}
                disabled={!hasAnalyzed}
                className={`flex items-center gap-1 rounded-full transition-all duration-200 disabled:opacity-20 ${
                  isBatmanMode
                    ? 'px-2.5 py-1 bg-violet-500/25 text-violet-200 ring-1 ring-violet-400/60 shadow-[0_0_10px_rgba(167,139,250,0.3)] font-bold tracking-wide scale-105'
                    : 'px-2 py-1 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 font-medium tracking-normal scale-100'
                }`}
                title="Toggle comic-book callouts"
                aria-label="Toggle comic-book callouts (keyboard shortcut: B)"
              >
                <Zap
                  size={isBatmanMode ? 15 : 13}
                  className={isBatmanMode ? 'text-violet-300 drop-shadow-[0_0_4px_rgba(167,139,250,0.8)]' : ''}
                />
                <span className={`text-[10px] transition-all duration-200 ${isBatmanMode ? 'uppercase tracking-wider' : ''}`}>
                  {isBatmanMode ? 'COMIC' : 'Comic'}
                </span>
                <kbd className={`text-[9px] px-1 rounded font-mono border leading-none py-px transition-all duration-200 ${
                  isBatmanMode
                    ? 'bg-violet-400/20 text-violet-300 border-violet-400/40'
                    : 'bg-white/10 text-white/50 border-white/20'
                }`}>B</kbd>
              </button>

              <button
                onClick={() => setIs4DEnabled(!is4DEnabled)}
                disabled={!videoSrc}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all disabled:opacity-20 ${
                  is4DEnabled
                    ? 'bg-cyan-500/20 text-cyan-400 ring-1 ring-cyan-500/40'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                }`}
                title="Toggle 4D biomechanics analysis panel"
              >
                <Box size={14} />
                <span>4D</span>
              </button>

              {/* FX reset — grouped apart with a leading separator */}
              <div className="w-px h-4 bg-zinc-700 mx-1" />
              <button
                onClick={clearVisualOverlays}
                disabled={!hasAnalyzed || (!isHeatmapEnabled && !isPathTraceEnabled)}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all disabled:opacity-20 ${
                  isHeatmapEnabled || isPathTraceEnabled
                    ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/40 hover:bg-amber-500/30'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                }`}
                title="Clear heatmap &amp; path (no playback restart)"
                aria-label="Clear heatmap and path trace overlays without restarting playback"
              >
                <RotateCcw size={14} />
                <span>Clear FX</span>
              </button>

              <div className="w-px h-4 bg-zinc-700 mx-1" />

              {/* Utility buttons */}
              <button
                onClick={() => setShowPanel(!showPanel)}
                className={`p-1.5 rounded transition-colors ${showPanel ? 'text-amber-400' : 'text-zinc-500 hover:text-white'}`}
                title="Toggle Analysis Panel"
              >
                <TrendingUp size={16} />
              </button>

              <button
                onClick={() => setShowCamera(true)}
                className="p-1.5 rounded text-zinc-500 hover:text-amber-400 transition-colors"
                title="Camera"
              >
                <Camera size={16} />
              </button>

              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-1.5 rounded text-zinc-500 hover:text-white transition-colors"
                title="Upload Video"
              >
                <Upload size={16} />
              </button>

              <button
                onClick={toggleMute}
                disabled={!videoSrc}
                className="p-1.5 rounded text-zinc-500 hover:text-white disabled:opacity-20 transition-colors"
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>

              <button
                onClick={toggleFullscreen}
                disabled={!videoSrc}
                className="p-1.5 rounded text-zinc-500 hover:text-white disabled:opacity-20 transition-colors"
                title="Fullscreen"
              >
                <Maximize size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Panel - expandable width */}
      {showPanel && (
        <div
          className={
            isMobile
              ? 'flex-shrink-0 w-full max-h-[45vh] overflow-y-auto border-t border-zinc-800'
              : `flex-shrink-0 transition-all duration-300 overflow-y-auto ${isPanelExpanded ? 'w-[500px]' : 'w-80'}`
          }
        >
          <AnalysisPanel
            objects={detectedObjects}
            sport={selectedSport}
            customFocus={userPrompt.trim() || null}
            selectedIndices={selectedLandmarks}
            isExpanded={isMobile ? false : isPanelExpanded}
            onToggleExpand={isMobile ? undefined : () => setIsPanelExpanded(!isPanelExpanded)}
            onExportReport={handleExportReport}
            isMobile={isMobile}
            onToggleSelect={(i) => {
              setSelectedLandmarks(prev => {
                const next = new Set(prev);
                if (next.has(i)) {
                  next.delete(i);
                } else {
                  next.add(i);
                }
                return next;
              });
            }}
            onClearSelection={() => setSelectedLandmarks(new Set())}
            onJumpToObject={(i) => {
              // Could implement jump-to functionality here
            }}
          />
          {/* Follow-up chat — ask questions about the athlete */}
          {hasAnalyzed && (
            <div className={`px-2 pb-2 ${isMobile ? 'border-t border-zinc-800 bg-zinc-950' : ''}`}>
              <AnalysisChat
                mediaItems={lastFrameBase64 ? [{
                  name: 'analyzed_frame.jpg',
                  mimeType: 'image/jpeg',
                  base64Data: lastFrameBase64,
                }] : []}
                analysisResult={detectedObjects.length > 0
                  ? detectedObjects.map(o => `${o.label} (${Math.round((o.confidence ?? 0.8) * 100)}%): ${o.description || ''}`).join('\n')
                  : null
                }
                analysisMode={selectedSport}
                videoRef={videoRef}
              />
            </div>
          )}
        </div>
      )}

      {/* Camera capture modal */}
      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}

      {/* Hidden elements */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="video/*,audio/*,.csv,.pdf,.doc,.docx,image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <canvas ref={canvasRef} className="hidden" />
      <canvas ref={trackingCanvasRef} width={TRACKING_RESOLUTION} height={TRACKING_RESOLUTION} className="hidden" />

      {/* Animations */}
      <style>{`
        @keyframes comic-sequence {
          0% {
            transform: translate(-50%, -50%) scale(0) rotate(-15deg);
            opacity: 0;
          }
          10% {
            transform: translate(-50%, -50%) scale(1.2) rotate(-3deg);
            opacity: 1;
          }
          20% {
            transform: translate(-50%, -50%) scale(1.0) rotate(-3deg);
            opacity: 1;
          }
          80% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.0) rotate(-3deg);
          }
          100% {
            transform: translate(-50%, -50%) scale(1.1) rotate(0deg);
            opacity: 0.8;
          }
        }
        .animate-comic-sequence {
          animation: comic-sequence 3s ease-out forwards;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      {/* 4D Analysis Panel Overlay */}
      {is4DEnabled && videoSrc && (
        <D4DPanel
          videoId={videoSrc}
          currentFrame={Math.floor(currentTime * 30)} // Assume 30fps
          isPlaying={isPlaying}
          videoDimensions={{
            width: videoNativeDims.width || stageSize,
            height: videoNativeDims.height || stageSize,
          }}
          videoRef={videoRef}
          onClose={() => setIs4DEnabled(false)}
        />
      )}
    </div>
  );
};

export default SlamMobileAnalyzer;
